import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import {
  STATUS_LIST,
  STATUS_LABEL,
  PHOTO_CATEGORIES,
  REPAIR_STAGES,
  canDeletePhoto,
  canEditOrderFull,
} from "@/lib/constants";
import { useAuth, useCap } from "@/context/AuthContext";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import BudgetTab from "@/components/BudgetTab";
import DiagnosisSection from "@/components/DiagnosisSection";
import WarrantyCard from "@/components/WarrantyCard";
import StageAccordion from "@/components/StageAccordion";
import DevicesPanel from "@/components/DevicesPanel";
import { PartsCostEditor, COSTS_UPDATED_EVENT } from "@/components/PartsCostEditor";
import SearchableSelect from "@/components/SearchableSelect";
import { stageForStatus, stageState } from "@/lib/stageProgress";
import {
  MAX_VIDEO_DURATION_S,
  MAX_ORIGINAL_MB,
  MAX_FINAL_MB,
  COMPRESS_ABOVE_MB,
  TARGET_MAX_SIDE,
  blobToDataURL,
  loadVideoMeta,
  captureThumbnail,
  pickRecorderMime,
  transcodeVideo,
  processVideoFile,
} from "@/lib/videoProcessing";
import {
  buildDiagnosisMessage,
  buildBudgetMessage,  buildFullUpdateMessage,
  buildReadyMessage,
  buildShareOrderMessage,
  buildWarrantyMessage,
  buildWaLink,
  copyToClipboard,
} from "@/lib/waMessages";
import {
  ArrowLeft,
  QrCode,
  Printer,
  Camera,
  Video,
  Play,
  Trash2,
  History,
  User,
  Building2,
  Wrench,
  Save,
  Copy,
  Eye,
  EyeOff,
  X,
  MessageCircle,
  Share2,
  Check,
  ClipboardCopy,
  ChevronDown,
} from "lucide-react";

// Helper: obtiene el status del "dispositivo activo" (primary=order, secundario=devices[i])
function getScopeDevStatus(order, targetDeviceId, isSecondaryView) {
  if (!isSecondaryView) return order.status;
  const dev = (order.devices_all || []).find((d) => d.id === targetDeviceId);
  return dev ? dev.status : order.status;
}

export default function OrderDetail() {
  const { id, deviceId: deviceIdParam } = useParams();
  const targetDeviceId = deviceIdParam || id;
  const isSecondaryView = !!deviceIdParam && deviceIdParam !== id;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [history, setHistory] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState("");
  const [photoCategory, setPhotoCategory] = useState("BEFORE");
  const [photoVisibleToClient, setPhotoVisibleToClient] = useState(false);
  const [tab, setTab] = useState("reparacion");
  const [qrOpen, setQrOpen] = useState(false);
  const [zoomPhoto, setZoomPhoto] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [diagnosisPreset, setDiagnosisPreset] = useState(null); // {text, seq}
  const [budget, setBudget] = useState({ items: [], total: 0, budget_status: "PENDING_QUOTE" });
  const [openStages, setOpenStages] = useState(null); // {diagnosis, before, during, budget, after}
  const [thermalCopyLabel, setThermalCopyLabel] = useState(null); // "COPIA CLIENTE" | "COPIA TALLER" | null
  const [deleteModal, setDeleteModal] = useState(null); // { password, confirmNumber, error, busy } | null
  // Modal informativo post-finalización en órdenes multi-equipo
  const [finishedModal, setFinishedModal] = useState(null); // { finishedDevice, pending: [...] } | null
  const [reconcileOpen, setReconcileOpen] = useState(false);

  const fileRef = useRef(null);

  async function refresh() {
    // Photos y budget se filtran por device_id: para primary usamos order.id (backend
    // hace fallback a legacy device_id=null). Para secundario, sólo del equipo.
    const photosUrl = `/orders/${id}/photos?device_id=${targetDeviceId}`;
    const budgetUrl = `/orders/${id}/budget?device_id=${targetDeviceId}`;
    const [o, h, p, b] = await Promise.all([
      api.get(`/orders/${id}`),
      api.get(`/orders/${id}/history`),
      api.get(photosUrl),
      api.get(budgetUrl).catch(() => ({ data: { items: [], total: 0, budget_status: "PENDING_QUOTE" } })),
    ]);
    setOrder(o.data);
    setHistory(h.data);
    setPhotos(p.data);
    setBudget(b.data);
  }

  useEffect(() => {
    refresh().catch((e) => toast.error(formatApiError(e)));
    api.get("/users").then((r) => setTechnicians(r.data.filter((u) => u.role === "technician" && u.active))).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data.filter((b) => b.active))).catch(() => {});
  }, [id, targetDeviceId]);

  // Auto-abre la etapa correspondiente al estado actual la primera vez que se carga la orden
  useEffect(() => {
    if (order && openStages === null) {
      const activeStatus = getScopeDevStatus(order, targetDeviceId, isSecondaryView);
      const current = stageForStatus(activeStatus);
      setOpenStages({
        diagnosis: current === "diagnosis" || current === "before", // Step 1 fusionado: diagnóstico + antes
        before: false, // "Antes" ahora vive dentro del Paso 1; no se usa como acordeón top-level
        during: current === "during",
        budget: current === "during", // mismo contexto visual que Reparación
        after: current === "after",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, targetDeviceId]);

  function toggleStage(key) {
    setOpenStages((prev) => ({ ...(prev || {}), [key]: !(prev && prev[key]) }));
  }

  if (!order) {
    return <div className="text-slate-500 font-mono">Cargando orden…</div>;
  }

  // scopeDev = "dispositivo activo": para primary es la orden misma; para secundario, el subdoc.
  const scopeDev = isSecondaryView
    ? ((order.devices_all || []).find((d) => d.id === targetDeviceId) || null)
    : order;

  if (isSecondaryView && !scopeDev) {
    return (
      <div className="max-w-2xl mx-auto card-base p-6 text-center" data-testid="device-not-found">
        <div className="text-slate-800 font-heading font-bold text-lg">Equipo no encontrado</div>
        <div className="mt-2 text-sm text-slate-600">El equipo solicitado no existe en esta orden.</div>
        <Link to={`/orders/${id}`} className="btn-secondary mt-4 inline-flex" data-testid="order-detail-back-from-missing">
          <ArrowLeft size={14} /> Volver a la orden
        </Link>
      </div>
    );
  }

  const isFullEdit = canEditOrderFull(user.role);
  // Técnico asignado: para primary usa order.technician_id, para secundario el subdoc
  const scopeTechnicianId = isSecondaryView ? scopeDev.technician_id : order.technician_id;
  const isAssignedTech = user.role === "technician" && scopeTechnicianId === user.id;
  const canEdit = isFullEdit || isAssignedTech;
  // Todos los roles internos pueden cambiar estado (validado también en backend)
  const canChangeStatus = ["admin", "supervisor", "technician", "reception"].includes(user.role);
  // Cualquier rol interno autorizado puede registrar garantía
  const canOpenWarranty = ["admin", "supervisor", "technician", "reception"].includes(user.role);
  // Any internal role can upload photos to any order (permissions validated in backend)
  const canUploadPhoto = ["admin", "supervisor", "technician", "reception"].includes(user.role);

  // Base URL pública para enlaces del cliente (QR físico + Compartir orden).
  // Se toma de env para poder apuntar al dominio oficial en producción
  // (https://taller.tupuntoplay.com) y caer al origin en dev/preview.
  const publicBaseUrl = (process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin).replace(/\/+$/, "");
  const qrValue = `${publicBaseUrl}/qr/${order.qr_token}`;

  async function changeStatus(newStatus) {
    setSaving(true);
    try {
      // PATCH /orders/{id}/devices/{device_id} funciona para AMBOS (primary/secundario)
      await api.patch(`/orders/${id}/devices/${targetDeviceId}`, { status: newStatus });
      toast.success(`Estado: ${STATUS_LABEL[newStatus]}`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  /**
   * Finaliza la reparación del equipo activo y, en órdenes multi-equipo,
   * muestra un modal informativo con los equipos restantes pendientes y la
   * posibilidad de saltar al siguiente. En órdenes de 1 equipo, idéntico a
   * changeStatus("REPAIRED") sin modal.
   */
  async function finishActiveDevice() {
    setSaving(true);
    try {
      await api.patch(`/orders/${id}/devices/${targetDeviceId}`, { status: "REPAIRED" });
      toast.success(`Estado: ${STATUS_LABEL["REPAIRED"]}`);
      // Releer orden para obtener devices_summary actualizado
      const o = await api.get(`/orders/${id}`);
      const fresh = o.data;
      setOrder(fresh);
      // Refrescar historial/presupuesto en paralelo
      Promise.all([
        api.get(`/orders/${id}/history`).then((r) => setHistory(r.data)).catch(() => {}),
        api.get(`/orders/${id}/budget?device_id=${targetDeviceId}`).then((r) => setBudget(r.data)).catch(() => {}),
      ]);
      const devs = fresh?.devices_all || [];
      if (devs.length > 1) {
        const finishedDev = devs.find((d) => d.id === targetDeviceId);
        const pending = devs.filter((d) => !["REPAIRED", "READY", "DELIVERED", "NOT_REPAIRABLE", "CLIENT_REJECTED"].includes(d.status));
        setFinishedModal({ finishedDevice: finishedDev, pending });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  function goToDevice(dev) {
    if (!dev) return;
    const href = dev.is_primary ? `/orders/${id}` : `/orders/${id}/devices/${dev.id}`;
    setFinishedModal(null);
    navigate(href);
  }

  async function saveField(field, value) {
    setSaving(true);
    try {
      // Campos SIEMPRE order-level (no viven en devices[]):
      const ORDER_ONLY_FIELDS = new Set(["observations", "current_branch_id", "problem_visible_to_client"]);
      if (ORDER_ONLY_FIELDS.has(field)) {
        await api.patch(`/orders/${id}`, { [field]: value });
      } else {
        // Campos device-level (diagnosis, technician_id, problem_reported, brand, model, serial, etc.)
        await api.patch(`/orders/${id}/devices/${targetDeviceId}`, { [field]: value });
      }
      toast.success("Guardado");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleMediaFile(file, categoryOverride, visibleOverride) {
    if (!file) return;
    const cat = categoryOverride || photoCategory;
    const vis = visibleOverride != null ? visibleOverride : photoVisibleToClient;
    const isVideo = (file.type || "").startsWith("video/");
    const isImage = (file.type || "").startsWith("image/");
    if (!isVideo && !isImage) {
      toast.error("Tipo de archivo no soportado. Sube una imagen o un video.");
      return;
    }
    setUploading(true);
    setUploadProgress(isVideo ? "Procesando video…" : "Procesando foto…");
    try {
      if (isImage) {
        if (file.size > 15 * 1024 * 1024) {
          throw new Error("Imagen máx 15 MB");
        }
        const dataUrl = await downscaleImage(file, 1400);
        setUploadProgress("Subiendo foto…");
        await api.post(`/orders/${id}/photos`, {
          order_id: id,
          device_id: targetDeviceId,
          category: cat,
          data: dataUrl,
          media_type: "photo",
          mime_type: "image/jpeg",
          visible_to_client: vis,
        });
        toast.success(vis ? "Foto subida (visible al cliente)" : "Foto subida");
      } else {
        const processed = await processVideoFile(file, (msg) => setUploadProgress(msg));
        setUploadProgress("Subiendo video…");
        await api.post(`/orders/${id}/photos`, {
          order_id: id,
          device_id: targetDeviceId,
          category: cat,
          data: processed.dataUrl,
          thumbnail: processed.thumbnail,
          duration: processed.duration,
          mime_type: processed.mimeType,
          media_type: "video",
          visible_to_client: vis,
        });
        toast.success(vis ? "Video subido (visible al cliente)" : "Video subido");
      }
      await refresh();
    } catch (e) {
      toast.error(e?.message || formatApiError(e));
    } finally {
      setUploading(false);
      setUploadProgress("");
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  /** Sube SECUENCIALMENTE todos los archivos recibidos (galería multi-selección).
   *  Procesa cada uno con el mismo pipeline que handleMediaFile (compresión,
   *  categoría, device_id, visibilidad al cliente). Evita doble carga reutilizando
   *  el guard `uploading` como semáforo global.
   */
  async function handleMediaFiles(fileList, categoryOverride, visibleOverride) {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const cat = categoryOverride || photoCategory;
    const vis = visibleOverride != null ? visibleOverride : photoVisibleToClient;
    if (files.length === 1) {
      await handleMediaFile(files[0], cat, vis);
      return;
    }
    setUploading(true);
    try {
      let okCount = 0;
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setUploadProgress(`Procesando ${i + 1} de ${files.length}…`);
        try {
          const isVideo = (f.type || "").startsWith("video/");
          const isImage = (f.type || "").startsWith("image/");
          if (!isVideo && !isImage) continue;
          if (isImage) {
            if (f.size > 15 * 1024 * 1024) {
              toast.error(`${f.name}: excede 15 MB`);
              continue;
            }
            const dataUrl = await downscaleImage(f, 1400);
            await api.post(`/orders/${id}/photos`, {
              order_id: id,
              device_id: targetDeviceId,
              category: cat,
              data: dataUrl,
              media_type: "photo",
              mime_type: "image/jpeg",
              visible_to_client: vis,
            });
          } else {
            const processed = await processVideoFile(f, (msg) => setUploadProgress(`${i + 1}/${files.length} · ${msg}`));
            await api.post(`/orders/${id}/photos`, {
              order_id: id,
              device_id: targetDeviceId,
              category: cat,
              data: processed.dataUrl,
              thumbnail: processed.thumbnail,
              duration: processed.duration,
              mime_type: processed.mimeType,
              media_type: "video",
              visible_to_client: vis,
            });
          }
          okCount++;
        } catch (e) {
          toast.error(`${f.name}: ${e?.message || formatApiError(e)}`);
        }
      }
      if (okCount > 0) toast.success(`${okCount} archivo${okCount > 1 ? "s" : ""} subido${okCount > 1 ? "s" : ""}${vis ? " (visibles al cliente)" : ""}`);
      await refresh();
    } finally {
      setUploading(false);
      setUploadProgress("");
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function deletePhoto(photoId, isVideo) {
    const label = isVideo ? "video" : "foto";
    if (!confirm(`¿Eliminar este ${label}?`)) return;
    try {
      await api.delete(`/orders/${id}/photos/${photoId}`);
      toast.success(`${isVideo ? "Video" : "Foto"} eliminado`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function togglePhotoVisibility(photo) {
    const isVideo = photo.media_type === "video";
    try {
      await api.patch(`/orders/${id}/photos/${photo.id}`, {
        visible_to_client: !photo.visible_to_client,
      });
      const label = isVideo ? "Video" : "Foto";
      toast.success(!photo.visible_to_client ? `${label} visible al cliente` : `${label} oculto`);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function copyOrderNumber() {
    navigator.clipboard.writeText(order.order_number);
    toast.success("Número copiado");
  }

  function printOrder(mode, variant) {
    const styleId = "dynamic-print-page-style";
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = styleId;
      document.head.appendChild(styleEl);
    }
    // Limpiar clases previas
    document.body.classList.remove("print-mode-letter", "print-mode-thermal", "print-mode-payment");
    if (mode === "thermal") {
      // Solo UNA copia por impresión. El usuario elige Cliente o Taller.
      const label = variant === "workshop" ? "COPIA TALLER" : "COPIA CLIENTE";
      setThermalCopyLabel(label);
      // Ancho REAL imprimible de una POS de rollo 80mm = 72mm.
      // El driver reporta el papel como 72mm de ancho; usar 80mm hacía que
      // Chrome redujera el ticket a una miniatura vertical.
      styleEl.textContent = "@page { size: 72mm auto; margin: 0; }";
      document.body.classList.add("print-mode-thermal");
    } else if (mode === "payment_ticket") {
      // 80mm thermal roll — área imprimible real = 72mm.
      styleEl.textContent = "@page { size: 72mm auto; margin: 0; }";
      document.body.classList.add("print-mode-payment");
    } else {
      // US Letter (Carta) — 8.5" x 11" — completamente aislada de reglas térmicas
      styleEl.textContent = "@page { size: letter; margin: 15mm; }";
      document.body.classList.add("print-mode-letter");
    }
    // Give the browser a tick to apply the styles + rerender before print
    setTimeout(() => {
      window.print();
    }, 80);
  }

  return (
    <>
    <div className="screen-only w-full max-w-6xl mx-auto space-y-6">
      {/* Header — ACCIONES en su propia línea (flex-wrap). El bloque de datos
         ocupa ancho completo debajo, para que fecha/número/cliente/equipo
         nunca queden en una columna estrecha cuando hay muchos botones. */}
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <button onClick={() => navigate(-1)} className="btn-ghost -ml-2" data-testid="order-back-button">
            <ArrowLeft size={16} /> Volver
          </button>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Acciones de la orden completa (imprimir/eliminar) sólo en la vista primaria */}
            {!isSecondaryView && (
              <>
                <button onClick={() => printOrder("letter")} className="btn-secondary" data-testid="order-print-button">
                  <Printer size={18} /> Imprimir carta
                </button>
                <button onClick={() => printOrder("thermal", "client")} className="btn-secondary" data-testid="order-print-thermal-client-button">
                  <Printer size={18} /> Ticket cliente
                </button>
                <button onClick={() => printOrder("thermal", "workshop")} className="btn-secondary" data-testid="order-print-thermal-workshop-button">
                  <Printer size={18} /> Ticket taller
                </button>
                <button
                  onClick={() => {
                    const phone = order.customer?.phone;
                    if (!phone) {
                      toast.error("Esta orden no tiene un número de WhatsApp registrado.");
                      return;
                    }
                    const msg = buildShareOrderMessage(order, qrValue);
                    const url = buildWaLink(phone, msg);
                    if (!url) {
                      toast.error("Esta orden no tiene un número de WhatsApp registrado.");
                      return;
                    }
                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                  className="btn-secondary text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  data-testid="order-share-whatsapp-button"
                  title="Compartir enlace público de la orden por WhatsApp"
                >
                  <Share2 size={18} /> 📲 Compartir orden
                </button>
              </>
            )}
            <button onClick={() => setQrOpen((v) => !v)} className="btn-secondary" data-testid="order-show-qr">
              <QrCode size={18} /> {qrOpen ? "Ocultar QR" : "QR"}
            </button>
            {!isSecondaryView && user.role === "admin" && (
              <button
                onClick={() => setDeleteModal({ password: "", confirmNumber: "", error: "", busy: false })}
                className="btn-secondary text-red-700 border-red-200 hover:bg-red-50"
                data-testid="order-delete-button"
                title="Eliminar orden (solo admin)"
              >
                <Trash2 size={18} /> Eliminar orden
              </button>
            )}
          </div>
        </div>

        {/* Datos de la orden — ancho completo */}
        <div className="space-y-3">
            {/* 1. Fecha de recepción */}
            <div data-testid="order-header-reception-date" className="flex items-center gap-2 text-sm">
              <span className="text-slate-500 font-mono text-[11px] uppercase tracking-widest font-semibold">
                Recibido:
              </span>
              <span className="text-slate-800 font-semibold">
                {order.created_at ? new Date(order.created_at).toLocaleString(undefined, {
                  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                }) : "—"}
              </span>
            </div>

            {/* 2. Número de orden — con etiqueta explícita */}
            <div data-testid="order-header-number-block">
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-700 font-bold">
                Número de orden
              </div>
              <button
                onClick={copyOrderNumber}
                className="font-mono text-slate-900 text-lg sm:text-xl font-extrabold tracking-wider hover:text-blue-700 flex items-center gap-1.5 mt-0.5"
                data-testid="order-number"
              >
                {order.order_number} <Copy size={14} className="opacity-40" />
              </button>
            </div>

            {/* 3. Cliente — etiqueta + card destacada */}
            {order.customer?.name && (
              <div data-testid="order-header-customer-block">
                <div className="text-[11px] font-mono uppercase tracking-widest text-slate-700 font-bold mb-1">
                  Cliente
                </div>
                <div
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 max-w-full"
                  data-testid="order-header-customer"
                >
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                    <User size={16} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-slate-900 text-base sm:text-lg leading-tight truncate uppercase">
                      {order.customer.name}
                    </div>
                    {order.customer?.phone && (
                      <div
                        className="mt-0.5 flex items-center gap-1.5 text-xs sm:text-sm text-slate-600 font-medium truncate"
                        data-testid="order-header-customer-phone"
                      >
                        <MessageCircle size={13} className="text-emerald-600 shrink-0" />
                        <span className="font-mono tracking-tight truncate">{order.customer.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 4. Equipo en reparación — CATEGORÍA + SUBCATEGORÍA/MODELO en MAYÚSCULAS */}
            <div data-testid="order-header-equipment-block">
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-700 font-bold mb-1 flex items-center gap-2 flex-wrap">
                <span>Equipo en reparación</span>
                {isSecondaryView && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-cyan-100 text-cyan-800 border border-cyan-300 px-2 py-0.5 text-[10px] font-bold"
                    data-testid="secondary-device-indicator"
                  >
                    Equipo secundario
                  </span>
                )}
              </div>
              <h1
                className="font-heading font-extrabold text-xl sm:text-3xl tracking-tight text-slate-900 leading-tight break-words uppercase"
                data-testid="order-header-equipment"
              >
                {[
                  scopeDev.category?.name,
                  scopeDev.subcategory?.name || scopeDev.model || scopeDev.brand,
                ].filter(Boolean).join(" ")}
              </h1>
              {(scopeDev.brand || scopeDev.model) && (scopeDev.subcategory?.name) && (
                <div className="text-slate-700 text-sm sm:text-base mt-1 font-semibold" data-testid="order-header-brand-model">
                  {scopeDev.brand}{scopeDev.brand && scopeDev.model ? " · " : ""}{scopeDev.model}
                </div>
              )}
            </div>

            {/* Estado */}
            <div className="pt-1">
              <StatusBadge status={scopeDev.status} size="lg" />
            </div>
          </div>
        </div>

      {/* Inline QR panel — rendered directly on the page, no modal/overlay */}
      {qrOpen && (
        <div data-testid="qr-inline-panel" className="flex flex-col items-center card-base p-5">
          <div
            data-testid="qr-inline-box"
            style={{
              background: "#ffffff",
              padding: "16px",
              borderRadius: "16px",
              width: "250px",
              height: "250px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <QRCode
              value={qrValue}
              size={218}
              bgColor="#ffffff"
              fgColor="#000000"
              level="M"
              style={{ height: "auto", maxWidth: "100%", width: "100%" }}
            />
          </div>
          <div className="text-blue-600 font-mono text-lg tracking-wider mt-4" data-testid="qr-inline-order-number">
            {order.order_number}
          </div>
          <div className="text-[11px] text-slate-500 mt-1 break-all text-center font-mono max-w-xs">
            {qrValue}
          </div>
          <button
            onClick={() => setQrOpen(false)}
            className="btn-secondary mt-4"
            data-testid="qr-inline-hide"
          >
            Ocultar QR
          </button>
        </div>
      )}

      {/* Multi-equipo: panel de equipos si la orden tiene >1 equipo. Se muestra
         SIEMPRE (incluso en vista secundaria) para poder saltar entre equipos. */}
      <DevicesPanel order={order} canManage={isFullEdit} onChanged={refresh} activeDeviceId={targetDeviceId} />

      {/* Botón "Corregir estado multi-equipo" — admin para reconciliar órdenes
         estructuralmente incoherentes (p.ej. orden DELIVERED pero device activo). */}
      {(order.devices_all || []).length > 1 && (
        <ReconcileTriggerButton
          order={order}
          onClick={() => setReconcileOpen(true)}
        />
      )}

      {/* Botón "Agregar equipo" — visible siempre para admin/supervisor (sólo desde la vista primaria) */}
      {isFullEdit && !isSecondaryView && (
        <AddDeviceInline orderId={order.id} onChanged={refresh} />
      )}

      {/* Tabs — sólo secciones secundarias. La vista principal es vertical. */}
      <div className="flex gap-1 border-b border-slate-200 -mx-1 px-1 overflow-x-auto">
        {[
          { id: "reparacion", label: "Reparación" },
          { id: "budget", label: "Presupuesto" },
          { id: "history", label: `Historial (${history.length})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`px-4 py-2.5 font-semibold text-sm border-b-2 -mb-px whitespace-nowrap transition-all ${
              tab === t.id ? "text-blue-700 border-blue-500" : "text-slate-500 border-transparent hover:text-slate-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "reparacion" && (
        <div className="max-w-4xl mx-auto space-y-5">
          {/* Hidden file input reutilizado por el flujo "Desde galería" del diagnóstico.
             SIN capture → galería del dispositivo. MULTIPLE → selección múltiple en Android/iOS. */}
          <input
            ref={fileRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={(e) => handleMediaFiles(e.target.files)}
            className="hidden"
            data-testid="photo-file-input"
          />

          {/* Banner multi-equipo: alerta visual permanente cuando la orden contiene
              más de un equipo. Dinámico (número total, posición, modelo, estado,
              pendientes). Objetivo: evitar que el técnico cargue diagnóstico/
              presupuesto/fotos/reparación de varios equipos dentro de uno solo. */}
          {(() => {
            const devs = order.devices_all || [];
            if (devs.length <= 1) return null;
            const activeIdx = devs.findIndex((d) => d.id === targetDeviceId);
            const activeNum = activeIdx >= 0 ? activeIdx + 1 : 1;
            const activeDev = devs[activeIdx] || scopeDev;
            const label = `${activeDev?.brand || ""} ${activeDev?.model || activeDev?.subcategory?.name || "Equipo"}`.trim();
            const pendingCount = devs.filter((d) => !["REPAIRED", "READY", "DELIVERED", "NOT_REPAIRABLE", "CLIENT_REJECTED"].includes(d.status)).length;
            const statusLabel = STATUS_LABEL[activeDev?.status] || activeDev?.status || "";
            const doneCount = devs.filter((d) => ["REPAIRED", "READY", "DELIVERED"].includes(d.status)).length;
            return (
              <div
                className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 sm:p-4 text-sm text-amber-900 flex items-start gap-3"
                data-testid="multi-device-scope-banner"
              >
                <div className="text-xl shrink-0 leading-none">⚠️</div>
                <div className="space-y-1.5 min-w-0 flex-1">
                  <div className="font-heading font-extrabold text-amber-900 uppercase tracking-wide">
                    Orden con {devs.length} equipos
                  </div>
                  <div className="text-base font-extrabold text-slate-900" data-testid="multi-device-scope-active">
                    ESTÁS TRABAJANDO EN EL EQUIPO {activeNum} DE {devs.length}: <span className="font-bold">{label}</span>
                    {statusLabel && <> · <span className="font-mono text-xs font-normal">{statusLabel}</span></>}
                  </div>
                  <div className="text-xs font-semibold" data-testid="multi-device-progress">{doneCount} de {devs.length} equipos finalizados</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="multi-device-summary">
                    {devs.map((d, i) => (
                      <Link
                        key={d.id}
                        to={d.is_primary ? `/orders/${order.id}` : `/orders/${order.id}/devices/${d.id}`}
                        className={`text-[11px] px-2 py-1 rounded-full border ${i === activeIdx ? "bg-slate-900 text-white border-slate-900" : "bg-white border-amber-300 text-amber-900 hover:border-amber-500"}`}
                        data-testid={`multi-device-summary-${i + 1}`}
                      >
                        Equipo {i + 1} — {STATUS_LABEL[d.status] || d.status}
                      </Link>
                    ))}
                  </div>
                  <div className="text-xs text-amber-800">
                    Los cambios realizados aquí (diagnóstico, presupuesto, fotos, reparación)
                    corresponden <b>únicamente a este equipo</b>.
                    {pendingCount > 0 && <> Pendientes en la orden: <b>{pendingCount}</b>.</>}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* 1. RECEPCIÓN + DIAGNÓSTICO — accordion fusionado
                Contiene (en este orden):
                  A) Problema reportado por el cliente
                  B) Estado al recibirlo (ex "Antes") — categoría de archivos BEFORE
                  C) Diagnóstico (texto + IA + evidencia DIAGNOSIS)
                Las categorías de fotos BEFORE y DIAGNOSIS siguen separadas internamente. */}
          {(() => {
            const beforeStage = REPAIR_STAGES.find((s) => s.key === "BEFORE");
            const beforePhotos = photos.filter((p) => p.category === "BEFORE");
            const dxState = stageState("diagnosis", scopeDev.status);
            const beforeState = stageState("before", scopeDev.status);
            const step1State = (dxState === "current" || beforeState === "current")
              ? "current"
              : ((dxState === "completed" && beforeState === "completed") ? "completed" : "future");
            return (
              <StageAccordion
                stepNumber={1}
                label="Recepción + Diagnóstico"
                description="Estado inicial, falla reportada y diagnóstico oficial"
                state={step1State}
                isOpen={!!(openStages && openStages.diagnosis)}
                onToggle={() => toggleStage("diagnosis")}
                testid="stage-diagnosis"
              >
                {/* A) PROBLEMA REPORTADO POR EL CLIENTE */}
                <div className="mb-5" data-testid="section-problem-reported">
                  <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                      A · Problema reportado por el cliente
                    </div>
                    {isFullEdit && !isSecondaryView && (
                      <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none" data-testid="toggle-problem-visibility">
                        <input
                          type="checkbox"
                          checked={!!order.problem_visible_to_client}
                          onChange={() => saveField("problem_visible_to_client", !order.problem_visible_to_client)}
                          className="w-4 h-4 accent-blue-600"
                        />
                        <span>
                          {order.problem_visible_to_client
                            ? <><Eye size={12} className="inline mb-0.5 text-green-600" /> Visible al cliente</>
                            : <><EyeOff size={12} className="inline mb-0.5 text-slate-500" /> Oculto</>
                          }
                        </span>
                      </label>
                    )}
                  </div>
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-3.5 text-base text-slate-900 whitespace-pre-wrap leading-relaxed font-medium" data-testid="problem-reported-text">
                    {scopeDev.problem_reported || <span className="text-slate-500 italic font-normal">Sin descripción cargada aún</span>}
                  </div>
                </div>

                {/* B) ESTADO AL RECIBIRLO — categoría BEFORE (archivos separados internamente) */}
                <div className="mb-5 pt-4 border-t border-slate-200">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
                    B · Estado al recibirlo
                    <span className="ml-2 normal-case tracking-normal text-slate-400">
                      · {beforePhotos.length} {beforePhotos.length === 1 ? "archivo" : "archivos"}
                    </span>
                  </div>
                  <StageMediaBody
                    stage={beforeStage}
                    photos={beforePhotos}
                    user={user}
                    canUploadPhoto={canUploadPhoto}
                    uploading={uploading}
                    uploadProgress={uploadProgress}
                    onUpload={(file, visible) => handleMediaFile(file, "BEFORE", visible)}
                    onUploadMany={(fileList, visible) => handleMediaFiles(fileList, "BEFORE", visible)}
                    onDelete={(pid, isVideo) => deletePhoto(pid, isVideo)}
                    onToggleVisibility={togglePhotoVisibility}
                    onPlayVideo={setPlayingVideo}
                    onZoomPhoto={setZoomPhoto}
                  />
                </div>

                {/* C) DIAGNÓSTICO — texto + IA + evidencia (categoría DIAGNOSIS) */}
                <div data-testid="section-diagnosis" className="pt-4 border-t border-slate-200">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">
                    C · Diagnóstico
                  </div>
                  <DiagnosisSection
                    order={order}
                    scopeDev={scopeDev}
                    deviceId={targetDeviceId}
                    isSecondaryView={isSecondaryView}
                    user={user}
                    photos={photos}
                    diagnosisPreset={diagnosisPreset}
                    onSaved={refresh}
                    onUploadMedia={() => {
                      setPhotoCategory("DIAGNOSIS");
                      setPhotoVisibleToClient(true);
                      setTimeout(() => fileRef.current?.click(), 100);
                    }}
                    uploading={uploading}
                  />
                </div>
              </StageAccordion>
            );
          })()}

          {/* 2-4. ETAPAS CRONOLÓGICAS — accordions
                Secuencia final: 2. Reparación → 3. Presupuesto → 4. Finalización.
                "Antes" (BEFORE) fue fusionado dentro del Paso 1 (Recepción + Diagnóstico),
                por eso se omite aquí; sus archivos siguen clasificados como categoría BEFORE.
                El Paso 3 (Presupuesto) reutiliza DuringStageActions + BudgetTab:
                NO duplica lógica ni datos; las reglas de validación (ej. necesitar presupuesto
                aprobado para finalizar) permanecen intactas en FinishRepairAction. */}
          {REPAIR_STAGES.filter((s) => s.key !== "BEFORE").map((stage) => {
            const stageKey = stage.key.toLowerCase(); // 'during' | 'after'
            const stageStepMap = { during: 2, after: 4 };
            const stepNumber = stageStepMap[stageKey];
            const stagePhotos = photos.filter((p) => p.category === stage.key);
            const stageAccordion = (
              <StageAccordion
                key={stage.key}
                stepNumber={stepNumber}
                label={stage.label}
                description={stage.description}
                state={stageState(stageKey, scopeDev.status)}
                isOpen={!!(openStages && openStages[stageKey])}
                onToggle={() => toggleStage(stageKey)}
                count={`${stagePhotos.length} ${stagePhotos.length === 1 ? "archivo" : "archivos"}`}
                testid={`stage-${stageKey}`}
              >
                <StageMediaBody
                  stage={stage}
                  photos={stagePhotos}
                  user={user}
                  canUploadPhoto={canUploadPhoto}
                  uploading={uploading}
                  uploadProgress={uploadProgress}
                  onUpload={(file, visible) => handleMediaFile(file, stage.key, visible)}
                  onUploadMany={(fileList, visible) => handleMediaFiles(fileList, stage.key, visible)}
                  onDelete={(pid, isVideo) => deletePhoto(pid, isVideo)}
                  onToggleVisibility={togglePhotoVisibility}
                  onPlayVideo={setPlayingVideo}
                  onZoomPhoto={setZoomPhoto}
                />

                {/* Reparación extendida disponible durante la reparación (during) y también en after (dentro de FinishRepairAction) */}
                {stageKey === "during" && (
                  <ExtendedRepairAction order={order} scopeDev={scopeDev} deviceId={targetDeviceId} isSecondaryView={isSecondaryView} onRefresh={refresh} canEdit={canChangeStatus} />
                )}

                {/* Acción principal de "Finalización": finalizar reparación + registrar retiro */}
                {stageKey === "after" && (
                  <FinishRepairAction
                    order={order}
                    scopeDev={scopeDev}
                    deviceId={targetDeviceId}
                    isSecondaryView={isSecondaryView}
                    budget={budget}
                    history={history}
                    userRole={user.role}
                    canChangeStatus={canChangeStatus}
                    canRegisterPickup={["admin", "supervisor", "reception"].includes(user.role)}
                    saving={saving}
                    onFinish={finishActiveDevice}
                    onMarkReady={() => changeStatus("READY")}
                    onRefresh={refresh}
                    onPrintPaymentTicket={() => printOrder("payment_ticket")}
                  />
                )}
              </StageAccordion>
            );

            // Paso 3 — Presupuesto: se renderiza ENTRE "during" (2) y "after" (4).
            // Reutiliza DuringStageActions tal cual: sin duplicar lógica, sin cambiar reglas.
            if (stageKey === "after") {
              const budgetStatus = budget?.budget_status || order.budget_status;
              const hasBudget = (budget?.items || []).length > 0;
              const budgetApproved = ["APPROVED", "AUTHORIZED", "COMPLETED"].includes(budgetStatus);
              const budgetDerivedState = (hasBudget && budgetApproved)
                ? "completed"
                : stageState("during", scopeDev.status);
              const budgetCount = hasBudget
                ? (budgetApproved ? "aprobado" : (budgetStatus ? "pendiente" : "en gestión"))
                : "sin presupuesto";
              return [
                  <StageAccordion
                    key="budget"
                    stepNumber={3}
                    label="Presupuesto"
                    description="Generación y aprobación del presupuesto"
                    state={budgetDerivedState}
                    isOpen={!!(openStages && openStages.budget)}
                    onToggle={() => toggleStage("budget")}
                    count={budgetCount}
                    testid="stage-budget"
                  >
                    <DuringStageActions
                      order={order}
                      scopeDev={scopeDev}
                      budget={budget}
                      onOpenBudget={() => {
                        // Mantener al técnico dentro del flujo vertical: en vez de saltar
                        // a la pestaña superior "Presupuesto", hacemos scroll al BudgetTab
                        // inline renderizado justo debajo (mismos datos, misma lógica).
                        const el = document.querySelector('[data-testid="stage-budget-inline-manager"]');
                        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                    />
                    {/* Gestión completa del presupuesto reutilizando BudgetTab tal cual.
                        No duplica lógica: BudgetTab se monta sólo cuando el técnico está
                        en la vista vertical ("reparacion"); si navega a la pestaña superior
                        "Presupuesto" (tab === "budget"), este bloque se desmonta porque
                        el contenedor padre deja de renderizarse. */}
                    <div
                      data-testid="stage-budget-inline-manager"
                      className="mt-4 pt-4 border-t border-slate-200"
                    >
                      <BudgetTab
                        order={order}
                        scopeDev={scopeDev}
                        deviceId={targetDeviceId}
                        isSecondaryView={isSecondaryView}
                        user={user}
                        onHistoryChange={refresh}
                      />
                    </div>
                  </StageAccordion>,
                stageAccordion,
              ];
            }

            return stageAccordion;
          })}

          {/* Otras fotos (categoría OTHER) — colapsable, se mantiene por compatibilidad */}
          {photos.some((p) => p.category === "OTHER") && (
            <details className="card-base p-5" data-testid="other-photos-collapsible">
              <summary className="cursor-pointer font-heading font-bold text-slate-700 flex items-center gap-2">
                <Camera size={16} /> Otras fotos ({photos.filter((p) => p.category === "OTHER").length})
              </summary>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {photos.filter((p) => p.category === "OTHER").map((p) => (
                  <MediaThumb
                    key={p.id}
                    photo={p}
                    label="Otras"
                    canManage={canDeletePhoto(user.role)}
                    onDelete={(pid, isVideo) => deletePhoto(pid, isVideo)}
                    onToggleVisibility={togglePhotoVisibility}
                    onPlayVideo={setPlayingVideo}
                    onZoomPhoto={setZoomPhoto}
                  />
                ))}
              </div>
            </details>
          )}

          {/* 4. INFORMACIÓN DE LA ORDEN — colapsable */}
          <details className="card-base p-5" data-testid="order-info-collapsible" open={false}>
            <summary className="cursor-pointer font-heading font-bold text-slate-700 flex items-center gap-2 select-none">
              <User size={16} /> {isSecondaryView ? "Información del equipo" : "Información de la orden"}
              <span className="text-xs font-mono text-slate-400 ml-1">(cliente · equipo · asignación · estado)</span>
            </summary>
            <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2 space-y-4">
                <Card title="Cliente" icon={User}>
                  <Field label="Nombre">{order.customer?.name}</Field>
                  <Field label="Teléfono">{order.customer?.phone}</Field>
                  {order.customer?.email && <Field label="Email">{order.customer.email}</Field>}
                </Card>

                <Card title="Equipo en reparación" icon={Wrench}>
                  <div className="rounded-xl bg-gradient-to-br from-blue-50 to-slate-50 border border-blue-100 p-4 mb-4" data-testid="equipment-hero">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Equipo</div>
                    <div className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900 leading-tight break-words" data-testid="equipment-hero-name">
                      {scopeDev.subcategory?.name || scopeDev.model || scopeDev.brand || "Equipo"}
                    </div>
                    <div className="mt-1 text-sm text-slate-600">
                      {scopeDev.brand && <span className="font-semibold text-slate-700">{scopeDev.brand}</span>}
                      {scopeDev.brand && scopeDev.model && <span className="text-slate-400"> · </span>}
                      {scopeDev.model && <span>{scopeDev.model}</span>}
                      {!scopeDev.brand && !scopeDev.model && scopeDev.category?.name && <span>{scopeDev.category.name}</span>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <HierField label="Categoría" value={scopeDev.category?.name} />
                    <HierField label="Tipo / Modelo" value={scopeDev.subcategory?.name} />
                    <HierField label="Marca" value={scopeDev.brand} />
                    <HierField label="Modelo" value={scopeDev.model} />
                    <HierField label="Serial / IMEI" value={scopeDev.serial} mono />
                    <HierField label="Accesorios" value={scopeDev.accessories} />
                  </div>
                </Card>

                {/* Observaciones son a nivel de la ORDEN completa; sólo se editan/muestran en la vista primaria */}
                {!isSecondaryView && (
                  <Card title="Observaciones">
                    {canEdit ? (
                      <EditableTextarea
                        initial={order.observations || ""}
                        onSave={(v) => saveField("observations", v)}
                        placeholder="Notas internas…"
                        testid="edit-observations"
                      />
                    ) : (
                      <div className="text-slate-800 whitespace-pre-wrap">{order.observations || <span className="text-slate-500">—</span>}</div>
                    )}
                  </Card>
                )}

                {/* WhatsApp share es sólo para la vista primaria (aún) */}
                {!isSecondaryView && <WhatsAppShareCard order={order} budget={budget} />}
              </div>

              <div className="space-y-4">
                <Card title="Estado" icon={Building2}>
                  {canChangeStatus ? (
                    <div className="grid grid-cols-1 gap-2">
                      {STATUS_LIST.map((s) => (
                        <button
                          key={s}
                          disabled={saving || s === scopeDev.status}
                          onClick={() => changeStatus(s)}
                          data-testid={`status-change-${s}`}
                          className={`py-2.5 px-3 rounded-xl text-sm font-semibold text-left transition-all border ${
                            s === scopeDev.status
                              ? "bg-blue-50 text-blue-700 border-blue-400"
                              : "bg-slate-50 text-slate-600 border-slate-200 hover:border-blue-400"
                          }`}
                        >
                          {STATUS_LABEL[s]}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <StatusBadge status={scopeDev.status} size="lg" />
                  )}
                </Card>

                {/* Garantía es a nivel de la orden completa; sólo se muestra en la vista primaria */}
                {!isSecondaryView && (
                  <WarrantyCard
                    order={order}
                    canOpenWarranty={canOpenWarranty}
                    onOpened={refresh}
                  />
                )}

                <Card title="Asignación">
                  <Field label="Sucursal recepción">{order.reception_branch?.name || "—"}</Field>
                  {isFullEdit ? (
                    <>
                      {/* La sucursal actual es propiedad de la ORDEN completa: sólo editable desde vista primaria */}
                      {!isSecondaryView && (
                        <>
                          <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mt-3 mb-1">Sucursal actual</div>
                          <select
                            value={order.current_branch_id || ""}
                            onChange={(e) => saveField("current_branch_id", e.target.value)}
                            className="input-base py-2 text-sm"
                            data-testid="edit-current-branch"
                          >
                            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                          </select>
                        </>
                      )}
                      <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mt-3 mb-1">Técnico</div>
                      <select
                        value={scopeDev.technician_id || ""}
                        onChange={(e) => saveField("technician_id", e.target.value || null)}
                        className="input-base py-2 text-sm"
                        data-testid="edit-technician"
                      >
                        <option value="">Sin asignar</option>
                        {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                    </>
                  ) : (
                    <>
                      {!isSecondaryView && <Field label="Sucursal actual">{order.current_branch?.name || "—"}</Field>}
                      <Field label="Técnico">{scopeDev.technician?.name || "Sin asignar"}</Field>
                      {user.role === "technician" && !scopeDev.technician_id && (
                        <ClaimRepairButton
                          orderId={order.id}
                          deviceId={isSecondaryView ? targetDeviceId : null}
                          onClaimed={refresh}
                        />
                      )}
                    </>
                  )}
                </Card>

                {/* Corrección retroactiva: orden ya finalizada + técnico vacío */}
                <RetroTechnicianFix
                  order={order}
                  scopeDev={scopeDev}
                  deviceId={targetDeviceId}
                  isSecondaryView={isSecondaryView}
                  technicians={technicians}
                  onRefresh={refresh}
                />

                <Card title="Metadata">
                  <Field label="Creada">{new Date(order.created_at).toLocaleString()}</Field>
                  <Field label="Última actualización">{new Date(order.updated_at).toLocaleString()}</Field>
                </Card>
              </div>
            </div>
          </details>
        </div>
      )}

      {tab === "budget" && (
        <>
          <BudgetTab order={order} scopeDev={scopeDev} deviceId={targetDeviceId} isSecondaryView={isSecondaryView} user={user} onHistoryChange={refresh} />
          <FinancialBreakdownCard orderId={order.id} deviceId={isSecondaryView ? targetDeviceId : null} multi={(order.devices_all || []).length > 1} />
        </>
      )}


      {tab === "history" && (
        <div className="card-base p-5">
          <div className="relative pl-6 space-y-4 before:absolute before:left-2 before:top-2 before:bottom-2 before:w-px before:bg-slate-100">
            {(isSecondaryView
              ? history.filter((h) => h.device_id === targetDeviceId)
              : history
            ).map((h, idx) => (
              <div key={h.id} className="relative" data-testid={`history-item-${idx}`}>
                <div className="absolute -left-[19px] top-1.5 w-3 h-3 rounded-full bg-blue-500 border-2 border-white shadow-md shadow-blue-600/20"></div>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="text-sm text-slate-900">{h.description}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {h.user_name} · <span className="font-mono">{h.user_role}</span>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 font-mono whitespace-nowrap">
                    {new Date(h.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
            {(isSecondaryView ? history.filter((h) => h.device_id === targetDeviceId) : history).length === 0 && (
              <div className="text-slate-500 text-sm">Sin historial.</div>
            )}
          </div>
        </div>
      )}

      {/* Photo zoom */}
      {zoomPhoto && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setZoomPhoto(null)}
          data-testid="photo-zoom-overlay"
        >
          <img src={zoomPhoto.data} alt="" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}

      {/* Video player */}
      {playingVideo && (
        <VideoPlayerOverlay video={playingVideo} onClose={() => setPlayingVideo(null)} />
      )}

      {/* AI moved inline inside DiagnosisSection — no more modal here */}
    </div>

    {/* PRINT-ONLY LETTER LAYOUT — visible only when Imprimir carta is invoked */}
    <div className="print-only-letter" data-testid="print-order-layout">
      <PrintOrder order={order} qrValue={qrValue} />
    </div>

    {/* PRINT-ONLY THERMAL 80mm LAYOUT — visible only when Imprimir 80mm is invoked */}
    <div className="print-only-thermal" data-testid="print-order-thermal-layout">
      <PrintOrderThermal order={order} qrValue={qrValue} copyLabel={thermalCopyLabel} />
    </div>

    {/* PRINT-ONLY PAYMENT TICKET (80mm) — visible only when "Imprimir ticket de pago" is invoked */}
    <div className="print-only-payment" data-testid="print-payment-ticket-layout">
      <PrintPaymentTicket order={order} budget={budget} qrValue={qrValue} />
    </div>

    {/* DELETE ORDER MODAL — solo admin */}
    {deleteModal && user.role === "admin" && (
      <DeleteOrderModal
        order={order}
        state={deleteModal}
        setState={setDeleteModal}
        onDeleted={() => navigate("/orders")}
      />
    )}

    {/* FINISHED (MULTI-DEVICE) MODAL — informa sobre equipos pendientes y permite saltar */}
    {finishedModal && (
      <FinishedDeviceModal
        order={order}
        finishedDevice={finishedModal.finishedDevice}
        pending={finishedModal.pending}
        onGoToNext={() => goToDevice(finishedModal.pending[0])}
        onClose={() => setFinishedModal(null)}
      />
    )}

    {/* ADMIN RECONCILE MODAL — solo con capability orders.correct_pickup */}
    {reconcileOpen && (
      <ReconcileDeviceModal
        order={order}
        onClose={() => setReconcileOpen(false)}
        onDone={() => { setReconcileOpen(false); refresh(); }}
      />
    )}
    </>
  );
}

function DeleteOrderModal({ order, state, setState, onDeleted }) {
  const requiredNumber = String(order.order_number || "").trim();
  const numberOk = state.confirmNumber.trim() === requiredNumber;
  const canSubmit = numberOk && state.password.length > 0 && !state.busy;

  async function handleDelete() {
    setState((s) => ({ ...s, busy: true, error: "" }));
    try {
      await api.delete(`/orders/${order.id}`, { data: { password: state.password } });
      toast.success(`Orden ${requiredNumber} eliminada`);
      onDeleted();
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo eliminar la orden";
      setState((s) => ({ ...s, busy: false, error: msg }));
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      onClick={() => !state.busy && setState(null)}
      role="dialog"
      aria-modal="true"
      data-testid="delete-order-modal"
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Trash2 size={20} className="text-red-700" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-extrabold text-slate-900 leading-tight">
              Eliminar orden {requiredNumber}
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Esta acción es <strong className="text-red-700">irreversible</strong>. Se borrarán todos los datos,
              fotos e historial de la orden. El movimiento queda registrado en el log de auditoría.
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest font-mono font-bold text-slate-600">
              Escribí el número de orden para confirmar
            </label>
            <input
              type="text"
              autoComplete="off"
              placeholder={requiredNumber}
              value={state.confirmNumber}
              onChange={(e) => setState((s) => ({ ...s, confirmNumber: e.target.value }))}
              className="mt-1 w-full input-base"
              data-testid="delete-order-confirm-number"
              disabled={state.busy}
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-mono font-bold text-slate-600">
              Tu contraseña de administrador
            </label>
            <input
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={state.password}
              onChange={(e) => setState((s) => ({ ...s, password: e.target.value }))}
              className="mt-1 w-full input-base"
              data-testid="delete-order-password"
              disabled={state.busy}
            />
          </div>

          {state.error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="delete-order-error">
              {state.error}
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            className="btn-secondary"
            onClick={() => setState(null)}
            disabled={state.busy}
            data-testid="delete-order-cancel"
          >
            Cancelar
          </button>
          <button
            className="btn-primary bg-red-700 hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleDelete}
            disabled={!canSubmit}
            data-testid="delete-order-confirm"
          >
            {state.busy ? "Eliminando..." : "Eliminar definitivamente"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PrintOrder({ order, qrValue }) {
  const receivedAt = new Date(order.created_at).toLocaleString();
  const now = new Date().toLocaleString();
  return (
    <div style={{ padding: "0", color: "#000", background: "#fff", fontSize: "12px", lineHeight: 1.35 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          borderBottom: "2px solid #000",
          paddingBottom: "10px",
          marginBottom: "14px",
        }}
      >
        <div>
          <div style={{ fontSize: "22px", fontWeight: 800, letterSpacing: "-0.02em" }}>
            Punto Play / PuntoCell
          </div>
          <div style={{ fontSize: "10px", color: "#444", textTransform: "uppercase", letterSpacing: "0.15em" }}>
            Servicio Técnico · Orden de Reparación
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: "9px", color: "#666", textTransform: "uppercase", letterSpacing: "0.15em" }}>
            N° de Orden
          </div>
          <div style={{ fontSize: "26px", fontWeight: 900, letterSpacing: "0.05em", fontFamily: "JetBrains Mono, monospace" }}>
            {order.order_number}
          </div>
          <div style={{ fontSize: "10px", color: "#444", marginTop: "2px" }}>
            Fecha ingreso: <b>{receivedAt}</b>
          </div>
        </div>
      </div>

      {/* Two columns: info + QR */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "12px" }}>
        <div style={{ flex: "1 1 60%" }}>
          <PrintSection title="Cliente">
            <PrintField label="Nombre">{order.customer?.name || "—"}</PrintField>
            <PrintField label="Teléfono">{order.customer?.phone || "—"}</PrintField>
            {order.customer?.email && <PrintField label="Email">{order.customer.email}</PrintField>}
          </PrintSection>

          <PrintSection title="Equipo">
            <PrintField label="Categoría">{order.category?.name || "—"}</PrintField>
            <PrintField label="Tipo / Modelo">{order.subcategory?.name || "—"}</PrintField>
            <PrintField label="Marca">{order.brand || "—"}</PrintField>
            <PrintField label="Modelo">{order.model || "—"}</PrintField>
            <PrintField label="Serial / IMEI">
              <span style={{ fontFamily: "JetBrains Mono, monospace" }}>{order.serial || "—"}</span>
            </PrintField>
            <PrintField label="Accesorios recibidos">{order.accessories || "—"}</PrintField>
          </PrintSection>

          <PrintSection title="Sucursal & Estado">
            <PrintField label="Sucursal recepción">{order.reception_branch?.name || "—"}</PrintField>
            <PrintField label="Sucursal actual">{order.current_branch?.name || "—"}</PrintField>
            <PrintField label="Estado actual">
              <b>{STATUS_LABEL[order.status] || order.status}</b>
            </PrintField>
            {order.technician?.name && <PrintField label="Técnico asignado">{order.technician.name}</PrintField>}
          </PrintSection>
        </div>

        {/* QR block */}
        <div style={{ flex: "0 0 190px", textAlign: "center" }}>
          <div
            style={{
              background: "#fff",
              border: "2px solid #000",
              padding: "8px",
              display: "inline-block",
              borderRadius: "6px",
            }}
          >
            <QRCode
              value={qrValue}
              size={170}
              bgColor="#ffffff"
              fgColor="#000000"
              level="M"
              style={{ display: "block" }}
            />
          </div>
          <div
            style={{
              fontFamily: "JetBrains Mono, monospace",
              fontSize: "14px",
              fontWeight: 700,
              marginTop: "6px",
              letterSpacing: "0.05em",
            }}
          >
            {order.order_number}
          </div>
          <div style={{ fontSize: "9px", color: "#666", marginTop: "4px" }}>
            Escanea el QR para acceder a la orden
          </div>
        </div>
      </div>

      {/* Problem reported */}
      <PrintBlock title="Problema reportado">
        <div style={{ whiteSpace: "pre-wrap", minHeight: "40px" }}>{order.problem_reported || "—"}</div>
      </PrintBlock>

      {/* Diagnosis */}
      {order.diagnosis && (
        <PrintBlock title="Diagnóstico">
          <div style={{ whiteSpace: "pre-wrap" }}>{order.diagnosis}</div>
        </PrintBlock>
      )}

      {/* Observations */}
      <PrintBlock title="Observaciones">
        <div style={{ whiteSpace: "pre-wrap", minHeight: "40px" }}>{order.observations || "—"}</div>
      </PrintBlock>

      {/* Signatures */}
      <div style={{ display: "flex", gap: "20px", marginTop: "30px" }}>
        <div style={{ flex: 1, borderTop: "1px solid #000", paddingTop: "4px", textAlign: "center", fontSize: "10px" }}>
          Firma del Cliente
        </div>
        <div style={{ flex: 1, borderTop: "1px solid #000", paddingTop: "4px", textAlign: "center", fontSize: "10px" }}>
          Firma Técnico / Recepción / Vendedor
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          marginTop: "16px",
          paddingTop: "6px",
          borderTop: "1px dashed #999",
          fontSize: "9px",
          color: "#666",
          textAlign: "center",
        }}
      >
        Documento impreso el {now} · Punto Play / PuntoCell — Servicio Técnico
      </div>
    </div>
  );
}

function PrintSection({ title, children }) {
  return (
    <div style={{ marginBottom: "10px" }}>
      <div
        style={{
          fontSize: "9px",
          textTransform: "uppercase",
          letterSpacing: "0.15em",
          color: "#666",
          borderBottom: "1px solid #ccc",
          paddingBottom: "2px",
          marginBottom: "4px",
          fontWeight: 700,
        }}
      >
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

function PrintField({ label, children }) {
  return (
    <div style={{ display: "flex", gap: "8px", fontSize: "11px", padding: "2px 0" }}>
      <div style={{ width: "130px", color: "#555", flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, wordBreak: "break-word" }}>{children}</div>
    </div>
  );
}

function PrintBlock({ title, children }) {
  return (
    <div style={{ marginBottom: "10px" }}>
      <div
        style={{
          fontSize: "9px",
          textTransform: "uppercase",
          letterSpacing: "0.15em",
          color: "#666",
          marginBottom: "3px",
          fontWeight: 700,
        }}
      >
        {title}
      </div>
      <div
        style={{
          border: "1px solid #bbb",
          padding: "8px",
          borderRadius: "4px",
          fontSize: "12px",
          background: "#fafafa",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children, innerRef }) {
  return (
    <div ref={innerRef} className="card-base p-5">
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={16} className="text-blue-600" />}
        <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-slate-600">{title}</h3>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex justify-between gap-3 text-sm py-1">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 text-right truncate">{children}</span>
    </div>
  );
}

/** HierField: label pequeña en uppercase gris, valor grande oscuro semibold — jerarquía clara escaneable */
function HierField({ label, value, mono = false }) {
  const has = value != null && value !== "";
  return (
    <div className="min-w-0" data-testid={`hier-field-${label.toLowerCase().replace(/[^a-z0-9]/g,"-")}`}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-0.5">{label}</div>
      <div className={`text-sm sm:text-base font-semibold text-slate-900 break-words ${mono ? "font-mono" : ""}`}>
        {has ? value : <span className="text-slate-400 font-normal">—</span>}
      </div>
    </div>
  );
}

// ==========================================
// WHATSAPP SHARE CARD (internal use only)
// ==========================================
function CopyBtn({ label, getText, testid, primary = false, disabled = false, disabledLabel }) {
  const [copied, setCopied] = useState(false);
  async function handle() {
    const t = typeof getText === "function" ? getText() : getText;
    if (!t) {
      toast.error(disabledLabel || "No hay información para copiar");
      return;
    }
    const ok = await copyToClipboard(t);
    if (ok) {
      setCopied(true);
      toast.success("Copiado ✓");
      setTimeout(() => setCopied(false), 1600);
    } else {
      toast.error("No se pudo copiar al portapapeles");
    }
  }
  const base = "flex items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed";
  const cls = primary
    ? `${base} bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white shadow-sm`
    : `${base} bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200`;
  return (
    <button type="button" onClick={handle} disabled={disabled} className={cls} data-testid={testid}>
      {copied ? <Check size={14} /> : primary ? <MessageCircle size={14} /> : <ClipboardCopy size={14} />}
      {copied ? "Copiado" : label}
    </button>
  );
}

function WhatsAppShareCard({ order, budget }) {
  const hasDiagnosis = !!(order.diagnosis || "").trim();
  const hasBudget = (budget?.items || []).length > 0;
  const statusMap = {
    RECEIVED: "Recibido", IN_DIAGNOSIS: "En diagnóstico",
    WAITING_APPROVAL: "Esperando aprobación", WAITING_PARTS: "Esperando repuesto",
    IN_REPAIR: "En reparación", TESTING: "En pruebas",
    REPAIRED: "Reparado", IN_TRANSFER: "En traslado a sucursal",
    READY: "Listo para retirar", DELIVERED: "Retirado por cliente",
    NOT_REPAIRABLE: "No reparable", CLIENT_REJECTED: "Cliente rechazó reparación",
    IN_WARRANTY: "En garantía",
  };
  return (
    <div className="card-base p-5" data-testid="wa-share-card">
      <div className="flex items-center gap-2 mb-1">
        <MessageCircle size={16} className="text-emerald-600" />
        <h3 className="font-heading font-bold text-sm uppercase tracking-wider text-slate-600">Compartir por WhatsApp</h3>
      </div>
      <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
        Copiá el texto listo para pegar al chat de tu cliente. No abre WhatsApp — vos elegís a quién enviarlo.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <CopyBtn primary label="Copiar diagnóstico"
          getText={() => buildDiagnosisMessage(order)}
          disabled={!hasDiagnosis} disabledLabel="Sin diagnóstico aún"
          testid="wa-copy-diagnosis" />
        <CopyBtn primary label="Copiar presupuesto"
          getText={() => buildBudgetMessage(order, budget)}
          disabled={!hasBudget} disabledLabel="Sin servicios en presupuesto"
          testid="wa-copy-budget" />
        <CopyBtn primary label="Copiar actualización"
          getText={() => buildFullUpdateMessage(order, budget)}
          testid="wa-copy-full" />
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono mb-1.5">Copiar campos individuales</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <CopyBtn label="N° Orden"
            getText={() => order.order_number}
            testid="wa-copy-order-number" />
          <CopyBtn label="Estado"
            getText={() => statusMap[order.status] || order.status}
            testid="wa-copy-status" />
          <CopyBtn label="Diagnóstico"
            getText={() => (order.diagnosis || "").trim() || null}
            disabled={!hasDiagnosis} disabledLabel="Sin diagnóstico aún"
            testid="wa-copy-diagnosis-only" />
          <CopyBtn label="Presupuesto"
            getText={() => {
              const items = budget?.items || [];
              if (!items.length) return null;
              const lines = items.map((it) => {
                const qty = it.quantity || 1;
                const sub = Number(it.subtotal ?? (it.unit_price || 0) * qty);
                const qtyPart = qty > 1 ? ` × ${qty}` : "";
                return `• ${it.name}${qtyPart} — $${sub.toFixed(2)}`;
              });
              lines.push(`Total: $${Number(budget.total ?? 0).toFixed(2)}`);
              return lines.join("\n");
            }}
            disabled={!hasBudget} disabledLabel="Sin servicios en presupuesto"
            testid="wa-copy-budget-only" />
        </div>
      </div>
    </div>
  );
}

// AIAnswer + renderInline moved to DiagnosisSection (AI is now inline there)

function EditableTextarea({ initial, preset, onSave, placeholder, testid }) {
  const [v, setV] = useState(initial);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setV(initial); setDirty(false); }, [initial]);
  useEffect(() => {
    if (preset && typeof preset === "object" && preset.text != null) {
      setV(preset.text);
      setDirty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset && preset.seq]);
  return (
    <div>
      <textarea
        value={v}
        onChange={(e) => { setV(e.target.value); setDirty(true); }}
        className="input-base min-h-[90px] text-sm"
        placeholder={placeholder}
        data-testid={testid}
      />
      {dirty && (
        <button onClick={() => onSave(v)} className="btn-primary mt-2" data-testid={`${testid}-save`}>
          <Save size={14} /> Guardar
        </button>
      )}
    </div>
  );
}

async function downscaleImage(file, maxSide = 1400) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, maxSide / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// =========================
// VIDEO PROCESSING HELPERS — importados desde el módulo compartido
// (reutilizados también por DiagnosisSection; comportamiento idéntico).
// =========================

function formatDuration(sec) {
  if (sec == null || !isFinite(sec)) return "";
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}


// =========================
// Reproductor de video con fallback:
//  - <video> nativo con controls/autoPlay/playsInline.
//  - Si el navegador no puede decodificar (HEVC en Android/Chrome, etc.) captura
//    onError y ofrece descargar el archivo original para reproducirlo en el
//    reproductor nativo del dispositivo, que sí acepta HEVC.
// =========================
function VideoPlayerOverlay({ video, onClose }) {
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const src = video.data || "";
  const isDataUrl = typeof src === "string" && src.startsWith("data:");
  const mime = video.mime_type || (isDataUrl ? (src.match(/^data:([^;]+);/) || [])[1] : "") || "video/mp4";
  const ext = (mime.split("/")[1] || "mp4").split(";")[0].replace("quicktime", "mov");
  const filename = `video-${String(video.id || "clip").slice(0, 8)}.${ext}`;

  async function handleDownload() {
    try {
      setDownloading(true);
      const res = await fetch(src);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo descargar el video");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/90 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="video-player-overlay"
    >
      <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        {!error ? (
          <video
            src={src}
            poster={video.thumbnail || undefined}
            controls
            autoPlay
            playsInline
            controlsList="nodownload"
            onError={() => setError(true)}
            className="w-full max-h-[85vh] rounded-xl bg-black"
            data-testid="video-player"
          >
            Tu navegador no soporta la reproducción de video.
          </video>
        ) : (
          <div className="rounded-2xl bg-white p-6 text-center" data-testid="video-player-error">
            <div className="text-4xl mb-2">🎬</div>
            <div className="font-heading font-bold text-lg text-slate-900 mb-1">
              Este navegador no puede reproducir el video
            </div>
            <div className="text-sm text-slate-600 mb-4">
              Fue grabado en un formato que Android no decodifica directamente (por ejemplo HEVC/H.265 de iPhone).
              Descargalo y abrilo desde la galería del teléfono, que sí lo soporta.
            </div>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="btn-primary"
              data-testid="video-player-download"
            >
              {downloading ? "Preparando…" : "⬇ Descargar video"}
            </button>
          </div>
        )}
        <button
          onClick={onClose}
          className="absolute -top-3 -right-3 bg-white text-slate-900 rounded-full w-9 h-9 flex items-center justify-center shadow-lg font-bold"
          data-testid="video-player-close"
        >
          ×
        </button>
      </div>
    </div>
  );
}

// =========================
// THERMAL 80mm PRINT LAYOUT — ONE copy per print job.
// El usuario decide entre "COPIA CLIENTE" y "COPIA TALLER" antes de imprimir.
// =========================
function PrintOrderThermal({ order, qrValue, copyLabel }) {
  // Fallback si por algún motivo no se pasó label (nunca debería ocurrir):
  const label = copyLabel || "COPIA CLIENTE";
  const note = label === "COPIA TALLER" ? "Pegar al equipo — control interno" : "Entregar al cliente";
  return <ThermalCopy order={order} qrValue={qrValue} label={label} note={note} />;
}

// Ticket térmico 80mm (POS80) — área imprimible REAL = 72mm.
// El wrapper ocupa 72mm completos con padding interno reducido (2mm por lado)
// para que el texto tenga ~68mm útiles y no toque el borde del rollo.
function ThermalCopy({ order, qrValue, label, note }) {
  const receivedAt = new Date(order.created_at).toLocaleString();
  const now = new Date().toLocaleString();
  const rule = { borderTop: "1px dashed #000", margin: "6px 0" };
  const wrap = {
    width: "72mm",
    maxWidth: "72mm",
    boxSizing: "border-box",
    color: "#000",
    background: "#fff",
    fontFamily: '"JetBrains Mono", "Courier New", monospace',
    fontSize: "13px",
    lineHeight: 1.4,
    padding: "3mm 2mm",
    margin: "0",
  };
  const center = { textAlign: "center" };
  const labelSmall = {
    fontSize: "11px",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    fontWeight: 700,
    marginTop: "8px",
    marginBottom: "2px",
  };
  const value = { fontSize: "13px", fontWeight: 500 };
  const receptionist = order.created_by_user?.name || "—";
  const allDevices = order.devices_all || [];
  const isMultiDevice = allDevices.length > 1;

  return (
    <div style={wrap} data-testid={`thermal-copy-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {/* Banner de tipo de copia */}
      <div style={{
        ...center,
        border: "2px solid #000",
        padding: "5px 4px",
        marginBottom: "6px",
        fontWeight: 800,
        letterSpacing: "0.08em",
      }}>
        <div style={{ fontSize: "14px" }}>{label}</div>
        <div style={{ fontSize: "10px", fontWeight: 500, marginTop: "1px" }}>{note}</div>
      </div>

      {/* Header taller */}
      <div style={center}>
        <div style={{ fontSize: "17px", fontWeight: 800, letterSpacing: "0.03em" }}>
          PUNTO PLAY / PUNTOCELL
        </div>
        <div style={{ fontSize: "11px", letterSpacing: "0.15em", marginTop: "1px" }}>
          SERVICIO TÉCNICO
        </div>
      </div>

      <div style={rule} />

      {/* N° de orden — bien grande */}
      <div style={center}>
        <div style={{ fontSize: "11px", letterSpacing: "0.15em" }}>N° DE ORDEN</div>
        <div style={{ fontSize: "22px", fontWeight: 800, letterSpacing: "0.06em", margin: "1px 0" }}>
          {order.order_number}
        </div>
        <div style={{ fontSize: "12px" }}>{receivedAt}</div>
        <div style={{ fontSize: "12px", marginTop: "2px", fontWeight: 700 }}>
          Estado: {(STATUS_LABEL[order.status] || order.status).toUpperCase()}
        </div>
      </div>

      <div style={rule} />

      {/* Cliente */}
      <div style={labelSmall}>Cliente</div>
      <div style={value}>{order.customer?.name || "—"}</div>
      {order.customer?.phone && <div style={value}>Tel: {order.customer.phone}</div>}

      {/* Equipo(s) */}
      {!isMultiDevice ? (
        <>
          <div style={labelSmall}>Equipo</div>
          <div style={{ ...value, fontWeight: 700 }}>{order.category?.name} / {order.subcategory?.name}</div>
          {(order.brand || order.model) && (
            <div style={value}>{[order.brand, order.model].filter(Boolean).join(" ")}</div>
          )}
          {order.serial && <div style={value}>Serial: {order.serial}</div>}
          {order.accessories && (
            <>
              <div style={labelSmall}>Accesorios</div>
              <div style={{ ...value, whiteSpace: "pre-wrap" }}>{order.accessories}</div>
            </>
          )}
          <div style={labelSmall}>Falla reportada</div>
          <div style={{ ...value, whiteSpace: "pre-wrap" }}>{order.problem_reported || "—"}</div>
        </>
      ) : (
        <>
          <div style={labelSmall}>Equipos ({allDevices.length})</div>
          {allDevices.map((d, i) => (
            <div key={d.id} style={{ marginTop: i === 0 ? "0" : "6px", paddingTop: i > 0 ? "4px" : "0", borderTop: i > 0 ? "1px dotted #000" : "none" }}>
              <div style={{ fontSize: "13px", fontWeight: 700 }}>
                #{i + 1} · {d.subcategory?.name || d.model || d.brand || "Equipo"}
              </div>
              {(d.brand || d.model) && (
                <div style={value}>{[d.brand, d.model].filter(Boolean).join(" ")}</div>
              )}
              {d.serial && <div style={value}>Serial: {d.serial}</div>}
              {d.problem_reported && (
                <div style={{ ...value, whiteSpace: "pre-wrap", fontSize: "12px" }}>
                  <b>Falla:</b> {d.problem_reported}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* Recepcionista + Sucursal */}
      <div style={labelSmall}>Recepcionista</div>
      <div style={value}>{receptionist}</div>
      <div style={labelSmall}>Sucursal</div>
      <div style={value}>{order.current_branch?.name || order.reception_branch?.name || "—"}</div>

      {order.observations && (
        <>
          <div style={labelSmall}>Observaciones</div>
          <div style={{ ...value, whiteSpace: "pre-wrap" }}>{order.observations}</div>
        </>
      )}

      <div style={rule} />

      {/* QR grande + leyenda */}
      <div style={center}>
        <div style={{ display: "inline-block", background: "#fff", padding: "5px" }}>
          <QRCode
            value={qrValue}
            size={200}
            bgColor="#ffffff"
            fgColor="#000000"
            level="M"
            style={{ display: "block" }}
          />
        </div>
        <div style={{ fontSize: "14px", fontWeight: 800, marginTop: "5px", letterSpacing: "0.06em" }}>
          {order.order_number}
        </div>
        <div style={{ fontSize: "11px", marginTop: "6px", lineHeight: 1.35, padding: "0 3mm" }}>
          Escanea el QR para hacer seguimiento a tu orden y consultar las políticas legales y de garantía.
        </div>
      </div>

      <div style={rule} />

      {/* Garantía nota */}
      <div style={{ ...center, fontSize: "11px", fontWeight: 700, letterSpacing: "0.04em" }}>
        GARANTÍA DE REPARACIÓN: 30 DÍAS
      </div>
      <div style={{ ...center, fontSize: "10px", marginTop: "1px" }}>
        Válida desde la entrega del equipo
      </div>

      <div style={rule} />

      {/* Footer */}
      <div style={{ ...center, fontSize: "10px", marginTop: "6px" }}>
        Impreso: {now}
      </div>
      <div style={{ ...center, fontSize: "10px", marginBottom: "10px", fontWeight: 700 }}>
        Punto Play / PuntoCell
      </div>

      {/* Firma del cliente — SOLO en COPIA TALLER */}
      {label === "COPIA TALLER" && (
        <>
          <div style={rule} />
          <div style={{ ...center, fontSize: "11px", fontWeight: 700, letterSpacing: "0.04em", marginTop: "4px" }}>
            FIRMA DEL CLIENTE
          </div>
          {/* Espacio suficiente para firmar */}
          <div style={{ height: "18mm", position: "relative", marginTop: "6px" }}>
            <div style={{
              position: "absolute",
              bottom: 0,
              left: "6mm",
              right: "6mm",
              borderTop: "1px solid #000",
            }} />
          </div>
          <div style={{ ...center, fontSize: "10px", marginTop: "3px", fontWeight: 600 }}>
            Firma
          </div>
          <div style={{ ...center, fontSize: "9px", marginTop: "4px", lineHeight: 1.3, padding: "0 1mm" }}>
            Al firmar, el cliente confirma que ha leído y acepta los Términos y Condiciones disponibles en el código QR de este ticket.
          </div>
          <div style={{ height: "4mm" }} />
        </>
      )}
    </div>
  );
}



// ==========================================
// PAYMENT TICKET (80mm thermal) — Ticket de pago con garantía de 30 días
// ==========================================
function PrintPaymentTicket({ order, budget, qrValue }) {
  const paidAt = order.pickup_at ? new Date(order.pickup_at) : new Date();
  const paidStr = paidAt.toLocaleString();
  // Garantía = 30 días desde la entrega/pago
  const warrantyEnd = new Date(paidAt.getTime() + 30 * 24 * 60 * 60 * 1000);
  const warrantyStr = warrantyEnd.toLocaleDateString();

  const rule = { borderTop: "1px dashed #000", margin: "4px 0" };
  const doubleRule = { borderTop: "2px solid #000", margin: "5px 0" };
  const wrap = {
    width: "72mm",
    maxWidth: "72mm",
    boxSizing: "border-box",
    color: "#000",
    background: "#fff",
    fontFamily: '"JetBrains Mono", "Courier New", monospace',
    fontSize: "11px",
    lineHeight: 1.35,
    padding: "3mm 2mm",
    margin: "0",
  };
  const center = { textAlign: "center" };
  const labelSmall = {
    fontSize: "9px",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontWeight: 700,
    marginTop: "6px",
    marginBottom: "2px",
  };
  const items = budget?.items || [];
  const total = Number(order.paid_amount ?? budget?.total ?? 0);
  const paymentMethod = order.payment_method || "—";
  const techName = order.technician?.name || "—";

  // Multi-equipo: si hay varios equipos, listamos cada uno con su modelo/serial y sus servicios asociados.
  const allDevices = order.devices_all || [];
  const isMultiDevice = allDevices.length > 1;

  function itemsForDevice(deviceId) {
    // Los ítems legacy tienen device_id=null y se atribuyen al primario (order.id).
    if (!deviceId) return [];
    return items.filter((it) => {
      const did = it.device_id ?? order.id;
      return did === deviceId;
    });
  }

  return (
    <div style={wrap} data-testid="payment-ticket-content">
      {/* Header logo/nombre del taller */}
      <div style={center}>
        <div style={{ fontSize: "14px", fontWeight: 800, letterSpacing: "0.02em" }}>
          PUNTO PLAY / PUNTOCELL
        </div>
        <div style={{ fontSize: "9px", letterSpacing: "0.12em" }}>
          SERVICIO TECNICO
        </div>
        <div style={{ fontSize: "10px", marginTop: "3px", fontWeight: 700 }}>
          TICKET DE PAGO
        </div>
      </div>

      <div style={doubleRule} />

      {/* Datos de la orden */}
      <div style={center}>
        <div style={{ fontSize: "9px", letterSpacing: "0.15em" }}>N° DE ORDEN</div>
        <div style={{ fontSize: "16px", fontWeight: 800, letterSpacing: "0.05em" }}>
          {order.order_number}
        </div>
      </div>
      <div style={{ ...center, fontSize: "10px", marginTop: "3px" }}>
        <div><b>Fecha de pago/entrega:</b></div>
        <div>{paidStr}</div>
      </div>

      <div style={rule} />

      {/* Cliente */}
      <div style={labelSmall}>Cliente</div>
      <div>{order.customer?.name || "—"}</div>
      {order.customer?.phone && <div>Tel: {order.customer.phone}</div>}

      {/* Equipo(s) reparado(s) + servicios */}
      {!isMultiDevice ? (
        <>
          <div style={labelSmall}>Equipo reparado</div>
          <div>{order.category?.name || ""} {order.subcategory?.name ? `/ ${order.subcategory.name}` : ""}</div>
          {(order.brand || order.model) && (
            <div>{[order.brand, order.model].filter(Boolean).join(" ")}</div>
          )}
          {order.serial && <div>IMEI/Serial: {order.serial}</div>}

          {order.diagnosis && (
            <>
              <div style={labelSmall}>Descripción de la reparación</div>
              <div style={{ whiteSpace: "pre-wrap", fontSize: "10px" }}>{order.diagnosis}</div>
            </>
          )}

          <div style={labelSmall}>Servicios / repuestos cobrados</div>
          {items.length === 0 ? (
            <div style={{ fontStyle: "italic", fontSize: "10px" }}>Sin ítems en el presupuesto</div>
          ) : (
            items.map((it) => {
              const qty = it.quantity || 1;
              const sub = Number(it.subtotal ?? (it.unit_price || 0) * qty);
              return (
                <div key={it.id} style={{ display: "flex", justifyContent: "space-between", gap: "6px" }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {it.name}{qty > 1 ? ` x${qty}` : ""}
                  </span>
                  <span style={{ whiteSpace: "nowrap" }}>${sub.toFixed(2)}</span>
                </div>
              );
            })
          )}
        </>
      ) : (
        <>
          <div style={labelSmall}>Equipos reparados ({allDevices.length})</div>
          {allDevices.map((d, idx) => {
            const devItems = itemsForDevice(d.id);
            return (
              <div key={d.id} style={{ marginTop: "5px", paddingTop: "3px", borderTop: idx > 0 ? "1px dotted #000" : "none" }}>
                <div style={{ fontWeight: 700, fontSize: "10px" }}>
                  #{idx + 1} · {d.subcategory?.name || d.model || d.brand || "Equipo"}
                </div>
                {(d.brand || d.model) && (
                  <div style={{ fontSize: "10px" }}>{[d.brand, d.model].filter(Boolean).join(" ")}</div>
                )}
                {d.serial && <div style={{ fontSize: "10px" }}>IMEI/Serial: {d.serial}</div>}
                {d.diagnosis && (
                  <div style={{ fontSize: "9px", whiteSpace: "pre-wrap", marginTop: "2px" }}>
                    <b>Diagnóstico:</b> {d.diagnosis}
                  </div>
                )}
                {devItems.length === 0 ? (
                  <div style={{ fontStyle: "italic", fontSize: "9px" }}>Sin servicios asociados</div>
                ) : (
                  devItems.map((it) => {
                    const qty = it.quantity || 1;
                    const sub = Number(it.subtotal ?? (it.unit_price || 0) * qty);
                    return (
                      <div key={it.id} style={{ display: "flex", justifyContent: "space-between", gap: "6px", fontSize: "10px" }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          {it.name}{qty > 1 ? ` x${qty}` : ""}
                        </span>
                        <span style={{ whiteSpace: "nowrap" }}>${sub.toFixed(2)}</span>
                      </div>
                    );
                  })
                )}
              </div>
            );
          })}
        </>
      )}

      <div style={rule} />

      {/* Total pagado + método de pago */}
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", fontWeight: 800 }}>
        <span>TOTAL PAGADO:</span>
        <span>${total.toFixed(2)}</span>
      </div>
      <div style={{ fontSize: "10px", marginTop: "3px" }}>
        <b>Método de pago:</b> {paymentMethod}
      </div>
      <div style={{ fontSize: "10px" }}>
        <b>Técnico responsable:</b> {techName}
      </div>

      <div style={doubleRule} />

      {/* Garantía */}
      <div style={{ ...center, border: "2px solid #000", padding: "6px", marginTop: "6px" }}>
        <div style={{ fontSize: "12px", fontWeight: 800, letterSpacing: "0.05em" }}>
          GARANTÍA DE REPARACIÓN
        </div>
        <div style={{ fontSize: "16px", fontWeight: 800, marginTop: "2px" }}>
          30 DÍAS
        </div>
        <div style={{ fontSize: "10px", marginTop: "3px" }}>
          Válida hasta:
        </div>
        <div style={{ fontSize: "13px", fontWeight: 800 }}>
          {warrantyStr}
        </div>
      </div>

      {/* QR pequeño de verificación */}
      <div style={{ ...center, marginTop: "8px" }}>
        <div style={{ display: "inline-block", background: "#fff", padding: "3px" }}>
          <QRCode value={qrValue} size={90} bgColor="#ffffff" fgColor="#000000" level="M" />
        </div>
        <div style={{ fontSize: "8px", marginTop: "3px" }}>
          Escaneá para consultar el estado
        </div>
      </div>

      <div style={rule} />

      <div style={{ ...center, fontSize: "9px", marginTop: "6px" }}>
        La garantía cubre la reparación efectuada y aplica a partir
        de la fecha de entrega. Presente este ticket para reclamos.
      </div>
      <div style={{ ...center, fontSize: "8px", marginTop: "8px" }}>
        ¡Gracias por confiar en nosotros!
      </div>
    </div>
  );
}


// ==========================================
// STAGE MEDIA BODY — cuerpo del accordion (form + galería), sin card wrapper
// (el wrapper lo aporta StageAccordion)
// ==========================================
function StageMediaBody({
  stage, photos, user, canUploadPhoto,
  uploading, uploadProgress, onUpload, onUploadMany, onDelete, onToggleVisibility,
  onPlayVideo, onZoomPhoto,
}) {
  const inputRef = useRef(null);
  const galleryRef = useRef(null);
  const [visible, setVisible] = useState(true); // default visible al cliente

  return (
    <div data-testid={`section-stage-${stage.key.toLowerCase()}`}>
      {canUploadPhoto && (
        <div className="mb-3">
          <label
            className="flex items-center gap-2 mb-2 text-xs text-slate-600 cursor-pointer select-none"
            data-testid={`stage-${stage.key.toLowerCase()}-visible-toggle`}
          >
            <input
              type="checkbox"
              checked={visible}
              onChange={(e) => setVisible(e.target.checked)}
              className="w-4 h-4 accent-blue-600"
            />
            <span>
              {visible
                ? <><Eye size={12} className="inline mb-0.5 text-green-600" /> Visible al cliente en el Portal Público</>
                : <><EyeOff size={12} className="inline mb-0.5 text-slate-500" /> Solo uso interno</>
              }
            </span>
          </label>
          {/* Dos inputs ocultos:
             - CÁMARA: accept imagen+video + capture="environment" → en Android/Samsung
               abre directamente la cámara trasera y permite elegir Foto o Video.
             - GALERÍA: sin capture, con multiple → permite seleccionar varias fotos/videos. */}
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            capture="environment"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f, visible);
              if (inputRef.current) inputRef.current.value = "";
            }}
            className="hidden"
            data-testid={`stage-${stage.key.toLowerCase()}-input`}
          />
          <input
            ref={galleryRef}
            type="file"
            accept="image/*,video/*"
            multiple
            onChange={(e) => {
              const files = e.target.files;
              if (files && files.length > 0) {
                if (onUploadMany) {
                  onUploadMany(files, visible);
                } else {
                  // Fallback de compatibilidad: subir secuencialmente de a uno.
                  Array.from(files).forEach((f) => onUpload(f, visible));
                }
              }
              if (galleryRef.current) galleryRef.current.value = "";
            }}
            className="hidden"
            data-testid={`stage-${stage.key.toLowerCase()}-gallery-input`}
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="btn-primary text-base py-3"
              data-testid={`stage-${stage.key.toLowerCase()}-camera-btn`}
            >
              {uploading ? (
                <><Camera size={18} /> {uploadProgress || "Subiendo…"}</>
              ) : (
                <><Camera size={18} /> Cámara</>
              )}
            </button>
            <button
              type="button"
              onClick={() => galleryRef.current?.click()}
              disabled={uploading}
              className="btn-secondary text-base py-3"
              data-testid={`stage-${stage.key.toLowerCase()}-gallery-btn`}
            >
              🖼️ Galería
            </button>
          </div>
          <div className="text-[11px] text-slate-500 mt-2 text-center">
            Videos: máximo 20 s · se convierten a MP4/H.264 automáticamente
          </div>
        </div>
      )}

      {photos.length === 0 ? (
        <div className="text-center text-slate-500 text-xs py-6 border border-dashed border-slate-200 rounded-xl" data-testid={`stage-${stage.key.toLowerCase()}-empty`}>
          Aún no hay fotos ni videos en esta etapa.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p) => (
            <MediaThumb
              key={p.id}
              photo={p}
              label={stage.label}
              canManage={canDeletePhoto(user.role)}
              onDelete={onDelete}
              onToggleVisibility={onToggleVisibility}
              onPlayVideo={onPlayVideo}
              onZoomPhoto={onZoomPhoto}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MediaThumb({ photo: p, label, canManage, onDelete, onToggleVisibility, onPlayVideo, onZoomPhoto }) {
  const isVideo = p.media_type === "video";
  const posterSrc = isVideo ? (p.thumbnail || null) : p.data;
  return (
    <div className="relative group card-base overflow-hidden aspect-square" data-testid={`photo-${p.id}`}>
      {isVideo ? (
        <button
          type="button"
          onClick={() => onPlayVideo(p)}
          className="w-full h-full block relative bg-slate-900"
          data-testid={`video-play-${p.id}`}
        >
          {posterSrc ? (
            <img src={posterSrc} alt={label} className="w-full h-full object-cover opacity-90" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs">Video</div>
          )}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-black/55 backdrop-blur-sm flex items-center justify-center border-2 border-white/80 shadow-lg">
              <Play size={26} className="text-white ml-1" fill="white" />
            </div>
          </div>
          {p.duration != null && (
            <div className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">
              {formatDuration(p.duration)}
            </div>
          )}
        </button>
      ) : (
        <img src={p.data} alt={label} className="w-full h-full object-cover cursor-zoom-in" onClick={() => onZoomPhoto(p)} />
      )}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/60 to-transparent px-2 py-2 text-[10px] text-slate-600 flex items-center justify-between pointer-events-none">
        <span className="truncate">{p.uploaded_by_name}</span>
        <span className="font-mono">{new Date(p.created_at).toLocaleDateString()}</span>
      </div>
      {isVideo && (
        <div
          className="absolute top-2 left-2 flex items-center gap-1 bg-slate-900/80 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full font-mono"
          data-testid={`media-type-video-${p.id}`}
        >
          <Video size={10} /> Video
        </div>
      )}
      {p.visible_to_client && (
        <div
          className={`absolute ${isVideo ? "top-8" : "top-2"} left-2 flex items-center gap-1 bg-green-500 text-white text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full font-mono`}
          data-testid={`photo-client-visible-${p.id}`}
        >
          <Eye size={10} /> Cliente
        </div>
      )}
      {canManage && (
        <button
          onClick={() => onToggleVisibility(p)}
          data-testid={`photo-toggle-visible-${p.id}`}
          title={p.visible_to_client ? "Ocultar al cliente" : "Mostrar al cliente"}
          className={`absolute top-2 right-9 p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all ${
            p.visible_to_client
              ? "bg-green-600 hover:bg-emerald-500 text-white"
              : "bg-slate-200 hover:bg-slate-600 text-white"
          }`}
        >
          {p.visible_to_client ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
      )}
      {canManage && (
        <button
          onClick={() => onDelete(p.id, isVideo)}
          data-testid={`photo-delete-${p.id}`}
          className="absolute top-2 right-2 bg-red-500 hover:bg-rose-500 text-white p-1.5 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}


// ==========================================
// DURING stage actions — Generar / Ver presupuesto + WhatsApp al cliente
// ==========================================
function DuringStageActions({ order, scopeDev, budget, onOpenBudget }) {
  const [copied, setCopied] = useState(false);
  const hasBudget = (budget?.items || []).length > 0;
  const isExpress = !!order.is_express;
  const budgetStatus = budget?.budget_status || order.budget_status;
  const isApproved = ["APPROVED", "AUTHORIZED", "COMPLETED"].includes(budgetStatus);
  const phone = order.customer?.phone || "";
  const waLink = phone ? buildWaLink(phone, hasBudget ? buildBudgetMessage(order, budget) : null) : "";

  async function copyBudget() {
    const t = buildBudgetMessage(order, budget);
    if (!t) {
      toast.error("Todavía no hay servicios en el presupuesto.");
      return;
    }
    const ok = await copyToClipboard(t);
    if (ok) {
      setCopied(true);
      toast.success("Presupuesto copiado al portapapeles");
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast.error("No se pudo copiar. Intentá desde HTTPS o pegá manualmente.");
    }
  }

  return (
    <div className="mb-4 rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4" data-testid="during-stage-actions">
      <div className="text-[10px] uppercase tracking-widest text-amber-700 font-mono mb-1">
        Acción principal
      </div>
      <div className="font-heading font-extrabold text-slate-900 text-base mb-1">
        {hasBudget ? "Presupuesto de esta reparación" : "¿Descubriste la falla?"}
      </div>
      <div className="text-xs text-slate-600 mb-3">
        {hasBudget
          ? (isApproved
              ? (isExpress
                  ? "Presupuesto preaprobado (Express). Podés seguir con la reparación."
                  : "Presupuesto aprobado. Podés continuar la reparación.")
              : "El cliente aún no aprobó. Copiá el presupuesto y enviálo por WhatsApp.")
          : "Generá el presupuesto con los servicios de reparación para enviárselo al cliente."}
      </div>

      <button
        type="button"
        onClick={onOpenBudget}
        className="btn-primary w-full text-base py-3 mb-2"
        data-testid={hasBudget ? "during-view-budget-btn" : "during-generate-budget-btn"}
      >
        <Wrench size={18} />
        {hasBudget ? "Ver / gestionar presupuesto" : "Generar presupuesto"}
      </button>

      {hasBudget && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={copyBudget}
            className="btn-secondary py-3 text-sm min-h-[48px]"
            data-testid="during-copy-budget-btn"
          >
            {copied ? <><Check size={16} /> Copiado</> : <><ClipboardCopy size={16} /> Copiar presupuesto</>}
          </button>
          {waLink ? (
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary py-3 text-sm min-h-[48px] bg-emerald-600 hover:bg-emerald-500"
              data-testid="during-whatsapp-btn"
            >
              <MessageCircle size={16} /> WhatsApp al cliente
            </a>
          ) : (
            <button
              type="button"
              disabled
              className="btn-secondary py-3 text-sm min-h-[48px] opacity-60 cursor-not-allowed"
              title="Cliente sin teléfono cargado"
              data-testid="during-whatsapp-btn-disabled"
            >
              <MessageCircle size={16} /> WhatsApp (sin teléfono)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ==========================================
// AFTER stage action — Enviar a pruebas / No reparable / Finalizar reparación / Registrar retiro
// ==========================================

function WarrantyWhatsAppButton({ order, budget }) {
  // Sólo tras confirmar retiro. isDelivered se evalúa por el ancestor,
  // pero el botón vive dentro de ese bloque; aun así, validamos defensivo.
  const isDelivered = order?.status === "DELIVERED" && !!order?.pickup_at;
  if (!isDelivered) return null;

  const phone = order?.customer?.phone;
  const hasPhone = !!phone;

  const publicBaseUrl = (process.env.REACT_APP_PUBLIC_BASE_URL || window.location.origin).replace(/\/+$/, "");
  const publicUrl = `${publicBaseUrl}/qr/${order.qr_token}`;

  const onClick = () => {
    if (!hasPhone) {
      toast.error("Esta orden no tiene un número de WhatsApp registrado.");
      return;
    }
    const msg = buildWarrantyMessage(order, publicUrl, budget);
    const url = buildWaLink(phone, msg);
    if (!url) {
      toast.error("No se pudo construir el enlace de WhatsApp.");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className="btn-primary w-full mt-2 bg-green-600 hover:bg-green-500 disabled:opacity-50"
      disabled={!hasPhone}
      data-testid="warranty-whatsapp-btn"
      title={hasPhone ? "Enviar mensaje de garantía por WhatsApp" : "Esta orden no tiene teléfono registrado"}
    >
      <MessageCircle size={16} /> 📲 Enviar garantía por WhatsApp
    </button>
  );
}


function CorrectPickupAction({ order, onRefresh }) {
  const canCorrect = useCap("orders.correct_pickup");
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Sólo cuando hay un retiro confirmado (status DELIVERED + pickup_at).
  const isDelivered = order?.status === "DELIVERED" && !!order?.pickup_at;
  if (!canCorrect || !isDelivered) return null;

  async function submit() {
    const r = reason.trim();
    if (!r) {
      toast.error("Debes indicar el motivo de la corrección.");
      return;
    }
    if (!window.confirm("¿Confirmás la corrección? El retiro será anulado y la orden volverá a 'Listo para retirar'. La auditoría interna conserva todo el historial.")) {
      return;
    }
    setSaving(true);
    try {
      await api.post(`/orders/${order.id}/correct-pickup`, { reason: r });
      toast.success("Retiro anulado. La orden volvió a 'Listo para retirar'.");
      setOpen(false);
      setReason("");
      await onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar la corrección");
    } finally {
      setSaving(false);
    }
  }

  const pickupDate = order.pickup_at ? new Date(order.pickup_at).toLocaleString() : "—";
  const amount = order.paid_amount != null ? `$${Number(order.paid_amount).toFixed(2)}` : "—";
  const method = order.payment_method || "—";
  const by = order.picked_up_by_name || "—";
  const eq = [order.brand, order.model].filter(Boolean).join(" ") || "—";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-secondary w-full mt-2 text-amber-700 border-amber-300 hover:bg-amber-50"
        data-testid="correct-pickup-btn"
      >
        ⚠️ Corregir entrega (admin)
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !saving && setOpen(false)}>
          <div className="bg-white rounded-2xl p-5 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()} data-testid="correct-pickup-modal">
            <div className="font-heading font-extrabold text-lg text-slate-900">⚠️ Corregir entrega</div>
            <div className="text-sm text-slate-700">
              Anulará el retiro registrado por error. La orden volverá a <b>Listo para retirar</b> y se ocultará del timeline público, pero la auditoría interna conservará el evento original junto con la corrección.
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 space-y-1">
              <div><b>Orden:</b> {order.order_number}</div>
              <div><b>Cliente:</b> {order.customer?.name || "—"}</div>
              <div><b>Equipo:</b> {eq}</div>
              <div><b>Fecha/hora de retiro:</b> {pickupDate}</div>
              <div><b>Registrado por:</b> {by}</div>
              <div><b>Método de pago:</b> {method}</div>
              <div><b>Monto registrado:</b> {amount}</div>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Motivo de la corrección *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej.: Se registró el retiro por error. El cliente aún no ha retirado el equipo."
                className="input-base min-h-[90px] text-sm mt-1"
                data-testid="correct-pickup-reason"
              />
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="btn-secondary flex-1"
                disabled={saving}
                data-testid="correct-pickup-cancel"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={saving || !reason.trim()}
                className="btn-primary flex-1 bg-amber-600 hover:bg-amber-500 disabled:opacity-50"
                data-testid="correct-pickup-submit"
              >
                {saving ? "Aplicando…" : "Anular retiro y volver a Listo para retirar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


function RetroTechnicianFix({ order, scopeDev, deviceId, isSecondaryView, technicians, onRefresh }) {
  const canReassign = useCap("production.reassign_historical");
  const dev = scopeDev || order;
  const isFinalized = !!dev.finalized_at;
  const hasTech = !!dev.technician_id;
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);

  // Sólo aparece cuando: (a) el usuario tiene la cap admin, (b) el equipo está finalizado, (c) sin técnico asignado.
  if (!canReassign || !isFinalized || hasTech) return null;

  const submit = async () => {
    if (!selected) {
      toast.error("Selecciona el técnico responsable");
      return;
    }
    if (!window.confirm("¿Confirmás la corrección retroactiva? La producción de esta reparación quedará atribuida al técnico seleccionado (respetando la fecha original de finalización).")) {
      return;
    }
    setSaving(true);
    try {
      const body = { technician_id: selected };
      if (isSecondaryView && deviceId && deviceId !== order.id) body.device_id = deviceId;
      await api.post(`/orders/${order.id}/reassign-technician`, body);
      toast.success("Producción atribuida correctamente al técnico");
      await onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar la corrección");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="retro-technician-fix">
      <div className="text-[10px] uppercase tracking-widest text-amber-800 font-mono mb-1">Corrección retroactiva</div>
      <div className="font-heading font-extrabold text-slate-900 text-base mb-1">
        Reparación finalizada sin técnico asignado
      </div>
      <div className="text-xs text-slate-700 mb-3">
        Esta reparación quedó finalizada sin un técnico responsable. Asigná el técnico que realmente realizó la reparación — la producción se atribuirá con la fecha original de finalización (<b>{new Date(dev.finalized_at).toLocaleDateString()}</b>) sin duplicar registros.
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <select
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          className="input-base py-2 text-sm flex-1"
          data-testid="retro-technician-select"
        >
          <option value="">Selecciona un técnico…</option>
          {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button
          type="button"
          onClick={submit}
          disabled={saving || !selected}
          className="btn-primary bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="retro-technician-submit"
        >
          {saving ? "Guardando…" : "Corregir técnico y producción"}
        </button>
      </div>
    </div>
  );
}


// ==========================================
// AFTER stage action — Enviar a pruebas / No reparable / Finalizar reparación / Registrar retiro
// ==========================================
function FinishRepairAction({ order, scopeDev, deviceId, isSecondaryView, budget, history, userRole, canChangeStatus, canRegisterPickup, saving, onFinish, onMarkReady, onRefresh, onPrintPaymentTicket }) {
  const dev = scopeDev || order;
  // Multi-equipo: ajusta copy/CTA para que quede explícito que la acción es por device.
  const devs = order.devices_all || [];
  const isMultiDevice = devs.length > 1;
  const activeIdx = devs.findIndex((d) => d.id === deviceId);
  const activeNum = activeIdx >= 0 ? activeIdx + 1 : 1;
  const deviceLabel = `${dev.brand || ""} ${dev.model || dev.subcategory?.name || "Equipo"}`.trim();
  const isFinalized = ["REPAIRED", "READY", "DELIVERED", "IN_WARRANTY", "NOT_REPAIRABLE"].includes(dev.status);
  const isDelivered = dev.status === "DELIVERED";
  // Para la trazabilidad acumulativa, el bloque "Retirado" debe seguir visible
  // aunque el estado haya avanzado a IN_WARRANTY u otro posterior al retiro.
  const wasDelivered = isDelivered || !!(order.pickup_at || dev.delivered_at || order.delivered_at);
  const isNotRepairable = dev.status === "NOT_REPAIRABLE";
  const isReady = dev.status === "READY";
  const isRepairedOnly = dev.status === "REPAIRED"; // Reparado pero aún no marcado como Listo para retirar

  // Trazabilidad acumulativa — se derivan desde history/datos existentes (no se
  // crea un nuevo storage; es sólo una lectura del `order_history` ya persistido).
  // Formato 12h con AM/PM, en separador local (DD/MM/YYYY · H:MM AM/PM).
  const fmtDT12 = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const date = d.toLocaleDateString("es-AR", { day: "numeric", month: "numeric", year: "numeric" });
      const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
      return `${date} · ${time}`;
    } catch { return ""; }
  };
  // Quién finalizó este device (primary → device_id puede ser null o igual a order.id
  // dependiendo del flujo histórico; secundario → device_id === deviceId).
  // Preferimos el evento específico REPAIR_FINALIZED (sólo lo escribe el flujo primario);
  // para devices secundarios usamos el STATUS_CHANGE a REPAIRED con el device_id correspondiente.
  const finalizedByName = (() => {
    if (!history || !history.length) return "";
    const matches = history.filter((h) => {
      const hDevId = h.device_id || null;
      if (!isSecondaryView) {
        // Primary: aceptar null o h.device_id === order.id
        if (h.event_type === "REPAIR_FINALIZED") return true;
        if (h.event_type === "STATUS_CHANGE" && h.to_value === "REPAIRED"
            && (hDevId === null || hDevId === order.id)) return true;
      } else {
        if (h.event_type === "STATUS_CHANGE" && h.to_value === "REPAIRED"
            && hDevId === deviceId) return true;
      }
      return false;
    });
    if (!matches.length) return "";
    // Entrada más antigua para capturar al autor original (no un rename posterior).
    matches.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    return matches[0].user_name || "";
  })();
  const techResponsibleName = dev.technician?.name || order.technician?.name || "";
  // Regla: Finalizar reparación requiere presupuesto oficial + técnico responsable asignado.
  // Excepción: si el usuario es Técnico y el equipo está libre, el backend lo autoasigna
  // al finalizar — por lo tanto el frontend no debe bloquearlo.
  const finalizeHasBudget = (budget?.items || []).length > 0;
  const hasAssignedTech = !!dev.technician_id;
  const isTechWillAutoAssign = userRole === "technician" && !hasAssignedTech;
  const finalizeHasTechnician = hasAssignedTech || isTechWillAutoAssign;
  const canFinalize = finalizeHasBudget && finalizeHasTechnician;
  const [nrOpen, setNrOpen] = useState(false);
  const [nrReason, setNrReason] = useState("");
  const [nrSaving, setNrSaving] = useState(false);
  const [testingSaving, setTestingSaving] = useState(false);
  const [waCopied, setWaCopied] = useState(false);

  // WhatsApp "Listo para retirar" — sólo tiene sentido cuando el equipo YA está reparado
  // Se muestra tanto para REPAIRED como para READY (útil para recordarle al cliente).
  // No lo mostramos en vista de equipo secundario porque el mensaje/teléfono es de la orden completa.
  const phone = order.customer?.phone || "";
  const readyText = buildReadyMessage(order, budget || {});
  const waLink = phone ? buildWaLink(phone, readyText) : "";

  async function handleCopyReady() {
    const ok = await copyToClipboard(readyText);
    if (ok) {
      setWaCopied(true);
      toast.success("Mensaje copiado. Pégalo en WhatsApp.");
      setTimeout(() => setWaCopied(false), 2500);
    } else {
      toast.error("No se pudo copiar. Intentá manualmente.");
    }
  }

  async function sendToTesting() {
    if (!window.confirm("¿Enviar a pruebas? La orden pasará al estado 'En pruebas'.")) return;
    setTestingSaving(true);
    try {
      // PATCH /orders/{id}/devices/{device_id} funciona uniformemente para primary y secundario
      await api.patch(`/orders/${order.id}/devices/${deviceId || order.id}`, { status: "TESTING" });
      toast.success("Enviada a pruebas");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTestingSaving(false);
    }
  }

  async function submitNotRepairable() {
    const reason = nrReason.trim();
    if (reason.length < 3) {
      toast.error("El motivo es obligatorio (mínimo 3 caracteres)");
      return;
    }
    setNrSaving(true);
    try {
      // device_id: null para primary, el id del subdoc para secundario
      const body = { reason };
      if (isSecondaryView) body.device_id = deviceId;
      await api.post(`/orders/${order.id}/not-repairable`, body);
      toast.success("Marcada como no reparable");
      setNrOpen(false);
      setNrReason("");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setNrSaving(false);
    }
  }

  return (
    <>
      {/* Banner activo cuando la reparación tiene marca de "Reparación extendida" */}
      {dev.extended_repair_active && (
        <ExtendedRepairAction order={order} scopeDev={scopeDev} deviceId={deviceId} isSecondaryView={isSecondaryView} onRefresh={onRefresh} canEdit={canChangeStatus} bannerOnly />
      )}

      {!isFinalized && canChangeStatus && (
        <div className="mt-4 space-y-2" data-testid="finish-repair-block">
          {/* Enviar a pruebas — opcional */}
          {dev.status !== "TESTING" && (
            <button
              type="button"
              onClick={sendToTesting}
              disabled={saving || testingSaving}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-base font-heading font-bold bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-60"
              data-testid="send-to-testing-btn"
            >
              🧪 Enviar a pruebas
            </button>
          )}

          {/* Reparación extendida — informa al cliente que tomará más tiempo */}
          <ExtendedRepairAction order={order} scopeDev={scopeDev} deviceId={deviceId} isSecondaryView={isSecondaryView} onRefresh={onRefresh} canEdit={canChangeStatus} buttonOnly />

          {/* No reparable — disponible siempre para los 4 roles */}
          <button
            type="button"
            onClick={() => setNrOpen(true)}
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-base font-heading font-bold bg-rose-600 text-white hover:bg-rose-500 transition-colors disabled:opacity-60"
            data-testid="not-repairable-btn"
          >
            🔴 No reparable / $5 revisión
          </button>

          {/* Finalizar reparación */}
          <div className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4">
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-mono mb-1">Acción principal</div>
            <div className="font-heading font-extrabold text-slate-900 text-base mb-1">
              {isMultiDevice ? `¿Terminaste la reparación del Equipo #${activeNum}?` : "¿Terminaste la reparación?"}
            </div>
            <div className="text-xs text-slate-600 mb-3">
              {isMultiDevice ? (
                <>Marca <b>únicamente este equipo</b> como <b>Reparado</b>. La orden ({devs.length} equipos) permanece abierta hasta resolver los demás. El retiro/entrega se registra aparte.</>
              ) : (
                <>Marca la orden como <b>Reparado</b>. No implica que el cliente ya retiró el equipo — el retiro/entrega se registra aparte.</>
              )}
            </div>
            {!finalizeHasTechnician && (
              <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="finalize-technician-required">
                ⚠️ <b>Debes asignar un técnico responsable antes de finalizar la reparación.</b> La producción se atribuye automáticamente al técnico asignado.
              </div>
            )}
            {isTechWillAutoAssign && (
              <div className="mb-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-xs text-emerald-900" data-testid="finalize-tech-will-auto-assign">
                ℹ️ <b>Esta reparación quedará registrada a tu nombre al finalizarla.</b> La producción se atribuirá a tu usuario.
              </div>
            )}
            {!finalizeHasBudget && (
              <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="finalize-budget-required">
                ⚠️ <b>Debes registrar el presupuesto de esta orden antes de finalizar la reparación.</b> El total del presupuesto será el monto contabilizado en la producción del técnico.
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                if (!finalizeHasTechnician) {
                  toast.error("Debes asignar un técnico responsable antes de finalizar la reparación.");
                  return;
                }
                if (!finalizeHasBudget) {
                  toast.error("Debes registrar el presupuesto de esta orden antes de finalizar la reparación.");
                  return;
                }
                const confirmMsg = isMultiDevice
                  ? `Estás finalizando únicamente:\n\nEquipo #${activeNum} · ${deviceLabel}\n\nLa orden contiene ${devs.length} equipos. ¿Confirmás?`
                  : "¿Confirmás que la reparación está terminada? La orden pasará a estado 'Reparado'.";
                if (window.confirm(confirmMsg)) {
                  onFinish();
                }
              }}
              disabled={saving || !canFinalize}
              className="btn-primary w-full text-base py-3 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="finish-repair-btn"
              title={canFinalize ? "" : "Asigna técnico y registra el presupuesto antes de finalizar"}
            >
              ✅ {isMultiDevice ? "Finalizar este equipo" : "Finalizar reparación"}
            </button>
          </div>
        </div>
      )}

      {isNotRepairable && (
        <div className="mt-4 rounded-2xl border-2 border-rose-300 bg-rose-50 p-4" data-testid="not-repairable-block">
          <div className="font-heading font-bold text-rose-900 flex items-center gap-2 mb-2">
            🔴 No reparable
          </div>
          {dev.not_repairable_reason && (
            <div className="text-sm text-slate-800">
              <div className="text-[10px] uppercase tracking-widest text-rose-700 font-mono mb-1">Motivo</div>
              <div className="whitespace-pre-wrap">{dev.not_repairable_reason}</div>
            </div>
          )}
          {(dev.not_repairable_by_user_name || dev.not_repairable_at) && (
            <div className="text-[11px] text-slate-500 mt-2">
              {dev.not_repairable_by_user_name && <>Registrado por: <b>{dev.not_repairable_by_user_name}</b></>}
              {dev.not_repairable_at && <> · {new Date(dev.not_repairable_at).toLocaleString()}</>}
            </div>
          )}
          {/* El registro de retiro/pago aplica a la ORDEN completa; sólo se muestra en la vista primaria */}
          {canRegisterPickup && !isSecondaryView && (
            <div className="mt-3">
              <PickupBlock order={order} onDone={onRefresh} />
            </div>
          )}
        </div>
      )}

      {isFinalized && !isNotRepairable && (
        <div className="mt-4 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-4" data-testid="finish-already">
          <div className="font-heading font-bold text-emerald-900 flex items-center gap-2 mb-1">
            <Check size={18} /> {isDelivered ? "Reparación finalizada" : (isReady ? "Listo para retirar" : "Reparación finalizada")}
          </div>
          {/* Trazabilidad permanente — se mantiene visible aunque el equipo ya haya sido retirado */}
          <div className="text-xs text-emerald-900 space-y-0.5 mb-3" data-testid="finalized-audit">
            {techResponsibleName && (
              <div><span className="font-semibold">Técnico responsable:</span> {techResponsibleName}</div>
            )}
            {finalizedByName && (
              <div><span className="font-semibold">Finalizado por:</span> {finalizedByName}</div>
            )}
            {dev.finalized_at && (
              <div data-testid="finalized-at">{fmtDT12(dev.finalized_at)}</div>
            )}
            {!wasDelivered && (
              <div className="text-emerald-800 pt-1">
                Estado actual: <b>{STATUS_LABEL[dev.status]}</b>.{" "}
                {isSecondaryView
                  ? "Este equipo queda listo. La entrega al cliente se registra desde la vista principal de la orden."
                  : (isRepairedOnly
                      ? "Notificá al cliente y marcá el equipo como listo para retirar."
                      : "La orden queda lista para el proceso de retiro/entrega al cliente.")}
              </div>
            )}
          </div>

          {/* Acciones de READY + registro de retiro: SOLO si todavía no fue entregado.
             Una vez entregado, estos controles desaparecen pero la cabecera de
             "Reparación finalizada" (arriba) permanece como trazabilidad. */}
          {!wasDelivered && isRepairedOnly && !isSecondaryView && canChangeStatus && (
            <div className="space-y-2 mb-3" data-testid="ready-actions">
              {/* Botón WhatsApp — copia el mensaje o abre wa.me si hay teléfono */}
              {phone ? (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-base font-heading font-bold bg-green-600 text-white hover:bg-green-500 transition-colors"
                  data-testid="ready-wa-open-btn"
                  onClick={() => { copyToClipboard(readyText); }}
                >
                  <MessageCircle size={18} /> WhatsApp: Tu equipo está listo
                </a>
              ) : (
                <button
                  type="button"
                  onClick={handleCopyReady}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-base font-heading font-bold bg-green-600 text-white hover:bg-green-500 transition-colors"
                  data-testid="ready-wa-copy-btn"
                >
                  <MessageCircle size={18} /> {waCopied ? "¡Copiado!" : "Copiar mensaje: Listo para retirar"}
                </button>
              )}
              {phone && (
                <button
                  type="button"
                  onClick={handleCopyReady}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-mono uppercase tracking-widest text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
                  data-testid="ready-wa-copy-btn-secondary"
                >
                  {waCopied ? "¡Copiado!" : "También podés copiar el texto"}
                </button>
              )}

              {/* Botón principal: marcar como Listo para retirar (READY) */}
              <button
                type="button"
                onClick={() => {
                  if (window.confirm("¿Marcar el equipo como 'Listo para retirar'? El cliente lo verá reflejado en su portal.")) {
                    onMarkReady?.();
                  }
                }}
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-base font-heading font-bold bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-60"
                data-testid="mark-ready-btn"
              >
                📦 Marcar como Listo para retirar
              </button>
            </div>
          )}

          {/* Retiro / pago aplica a la ORDEN completa; sólo en vista primaria Y si todavía no fue retirado */}
          {!wasDelivered && canRegisterPickup && !isSecondaryView && (
            <PickupBlock order={order} onDone={onRefresh} />
          )}
        </div>
      )}

      {wasDelivered && (
        <div className="mt-4 rounded-2xl border-2 border-slate-200 bg-slate-50 p-4" data-testid="delivered-block">
          <div className="font-heading font-bold text-slate-800 flex items-center gap-2 mb-1">
            <Check size={18} /> Retirado por el cliente
          </div>
          <div className="text-xs text-slate-700 space-y-0.5" data-testid="delivered-audit">
            {(order.pickup_at || dev.delivered_at) && (
              <div data-testid="pickup-at">{fmtDT12(order.pickup_at || dev.delivered_at)}</div>
            )}
            {order.picked_up_by_name && (
              <div><span className="font-semibold">Registrado por:</span> {order.picked_up_by_name}</div>
            )}
            {order.paid_amount != null && (
              <div>
                <span className="font-semibold">Cobro:</span>{" "}
                <b>${Number(order.paid_amount).toFixed(2)}</b>
                {order.payment_method ? <> · {order.payment_method}</> : null}
              </div>
            )}
          </div>
          {/* Impresión de ticket de pago — sólo desde la vista primaria */}
          {!isSecondaryView && (
            <>
              <button
                type="button"
                onClick={onPrintPaymentTicket}
                className="btn-primary w-full mt-3 bg-emerald-600 hover:bg-emerald-500"
                data-testid="print-payment-ticket-btn"
              >
                <Printer size={16} /> 🖨️ Imprimir ticket de pago
              </button>
              <WarrantyWhatsAppButton order={order} budget={budget} />
              <CorrectPickupAction order={order} onRefresh={onRefresh} />
              <div className="text-[10px] text-slate-500 mt-1 text-center">
                Incluye garantía de 30 días · Optimizado para impresora térmica
              </div>
            </>
          )}
        </div>
      )}

      {/* Modal — No reparable */}
      {nrOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !nrSaving && setNrOpen(false)}>
          <div className="bg-white rounded-2xl p-5 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()} data-testid="not-repairable-modal">
            <div className="font-heading font-extrabold text-lg text-slate-900">
              🔴 Marcar reparación como no reparable
            </div>
            <div className="text-sm text-slate-600">
              Registrará el motivo, tu usuario y la fecha/hora. El cliente lo verá en el portal público.
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-slate-700 font-bold block mb-1">
                Motivo de no reparación *
              </label>
              <textarea
                value={nrReason}
                onChange={(e) => setNrReason(e.target.value)}
                placeholder="Ej: Problema en tarjeta madre. No reparable."
                className="input-base min-h-[100px] text-sm"
                data-testid="not-repairable-reason"
                autoFocus
              />
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button
                type="button"
                onClick={() => setNrOpen(false)}
                disabled={nrSaving}
                className="btn-secondary text-sm"
                data-testid="not-repairable-cancel"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={submitNotRepairable}
                disabled={nrSaving || nrReason.trim().length < 3}
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-heading font-bold bg-rose-600 text-white hover:bg-rose-500 disabled:opacity-60"
                data-testid="not-repairable-confirm"
              >
                Confirmar no reparable
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Botón + banner + modal de "Reparación extendida". Compone/consume su propio estado
// interno para poder colocarse en múltiples etapas (during y after) sin duplicar lógica.
function ExtendedRepairAction({ order, scopeDev, deviceId, isSecondaryView, onRefresh, canEdit, buttonOnly = false, bannerOnly = false }) {
  const [open, setOpen] = useState(false);
  const dev = scopeDev || order;
  const isActive = !!dev.extended_repair_active;

  if (bannerOnly) {
    if (!isActive) return null;
    return (
      <>
        <ExtendedRepairBanner
          order={order}
          scopeDev={dev}
          deviceId={deviceId}
          isSecondaryView={isSecondaryView}
          onEdit={() => setOpen(true)}
          onRefresh={onRefresh}
          canEdit={canEdit}
        />
        {open && (
          <ExtendedRepairModal
            order={order}
            scopeDev={dev}
            deviceId={deviceId}
            isSecondaryView={isSecondaryView}
            onClose={() => setOpen(false)}
            onSaved={() => { setOpen(false); onRefresh?.(); }}
          />
        )}
      </>
    );
  }

  if (buttonOnly) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          disabled={!canEdit}
          className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-base font-heading font-bold bg-amber-500 text-white hover:bg-amber-400 transition-colors disabled:opacity-60"
          data-testid="extended-repair-btn"
        >
          🕐 {isActive ? "Editar reparación extendida" : "Reparación extendida"}
        </button>
        {open && (
          <ExtendedRepairModal
            order={order}
            scopeDev={dev}
            deviceId={deviceId}
            isSecondaryView={isSecondaryView}
            onClose={() => setOpen(false)}
            onSaved={() => { setOpen(false); onRefresh?.(); }}
          />
        )}
      </>
    );
  }

  // Full mode: banner + inline button (used in during-stage)
  return (
    <>
      {isActive && (
        <ExtendedRepairBanner
          order={order}
          scopeDev={dev}
          deviceId={deviceId}
          isSecondaryView={isSecondaryView}
          onEdit={() => setOpen(true)}
          onRefresh={onRefresh}
          canEdit={canEdit}
        />
      )}
      {canEdit && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-base font-heading font-bold bg-amber-500 text-white hover:bg-amber-400 transition-colors"
          data-testid="extended-repair-btn"
        >
          🕐 {isActive ? "Editar reparación extendida" : "Reparación extendida"}
        </button>
      )}
      {open && (
        <ExtendedRepairModal
          order={order}
          scopeDev={dev}
          deviceId={deviceId}
          isSecondaryView={isSecondaryView}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); onRefresh?.(); }}
        />
      )}
    </>
  );
}

function ExtendedRepairBanner({ order, scopeDev, deviceId, isSecondaryView, onEdit, onRefresh, canEdit }) {
  const dev = scopeDev || order;
  const deadline = dev.extended_repair_deadline
    ? new Date(dev.extended_repair_deadline)
    : null;
  const [busy, setBusy] = useState(false);
  async function clear() {
    if (!window.confirm("¿Quitar la marca de Reparación extendida? El estado y el progreso se conservan.")) return;
    setBusy(true);
    try {
      const url = isSecondaryView
        ? `/orders/${order.id}/extended-repair?device_id=${deviceId}`
        : `/orders/${order.id}/extended-repair`;
      await api.delete(url);
      toast.success("Reparación extendida desactivada");
      onRefresh?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-3 rounded-2xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="extended-repair-banner">
      <div className="flex items-center gap-2 font-heading font-bold text-amber-900 mb-1">
        🕐 Reparación extendida
      </div>
      <div className="text-sm text-slate-800">
        Esta reparación requiere más tiempo del inicialmente estimado debido a la complejidad del proceso.
      </div>
      {deadline && (
        <div className="mt-2 text-sm">
          <span className="text-[10px] uppercase tracking-widest font-mono text-amber-800">Fecha estimada de finalización</span>
          <div className="font-heading font-extrabold text-amber-900 text-lg" data-testid="extended-repair-banner-deadline">
            {deadline.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
          {dev.extended_repair_business_days ? (
            <div className="text-[11px] text-slate-500 font-mono">
              {dev.extended_repair_business_days} día(s) hábil(es) desde {dev.extended_repair_set_at ? new Date(dev.extended_repair_set_at).toLocaleDateString() : ""}
            </div>
          ) : null}
        </div>
      )}
      {canEdit && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button type="button" onClick={onEdit} className="btn-secondary text-xs" data-testid="extended-repair-edit-btn">
            Editar fecha
          </button>
          <button type="button" onClick={clear} disabled={busy} className="btn-secondary text-xs" data-testid="extended-repair-clear-btn">
            {busy ? "…" : "Quitar marca"}
          </button>
        </div>
      )}
    </div>
  );
}

function ExtendedRepairModal({ order, scopeDev, deviceId, isSecondaryView, onClose, onSaved }) {
  const dev = scopeDev || order;
  const initialDays = dev.extended_repair_active ? (dev.extended_repair_business_days || 3) : 3;
  const [businessDays, setBusinessDays] = useState(initialDays);
  const [busy, setBusy] = useState(false);

  // Preview de la fecha calculada en cliente (el backend recomputa la fecha real igual)
  const previewDeadline = useMemo(() => {
    const d = new Date();
    let remaining = businessDays;
    while (remaining > 0) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0 && d.getDay() !== 6) remaining -= 1;
    }
    return d;
  }, [businessDays]);

  async function submit() {
    if (businessDays < 1 || businessDays > 15) return;
    setBusy(true);
    try {
      const body = { business_days: businessDays };
      if (isSecondaryView) body.device_id = deviceId;
      await api.post(`/orders/${order.id}/extended-repair`, body);
      toast.success("Reparación marcada como extendida");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
      role="dialog"
      aria-modal="true"
      data-testid="extended-repair-modal"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 border border-slate-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 text-xl">
            🕐
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-extrabold text-slate-900 leading-tight">
              Reparación extendida
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Esta reparación requiere más tiempo del inicialmente estimado. Elegí la nueva fecha estimada de finalización (máximo 15 días hábiles).
            </p>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          <label className="text-xs uppercase tracking-widest font-mono font-bold text-slate-600">
            Días hábiles adicionales
          </label>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={1}
              max={15}
              value={businessDays}
              onChange={(e) => setBusinessDays(parseInt(e.target.value, 10) || 1)}
              className="flex-1"
              disabled={busy}
              data-testid="extended-repair-range"
            />
            <input
              type="number"
              min={1}
              max={15}
              value={businessDays}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!isNaN(v)) setBusinessDays(Math.max(1, Math.min(15, v)));
              }}
              className="w-20 input-base py-2 font-mono text-center"
              disabled={busy}
              data-testid="extended-repair-days-input"
            />
          </div>

          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
            <div className="text-[10px] uppercase tracking-widest font-mono text-amber-800 mb-1">
              Fecha estimada de finalización
            </div>
            <div className="font-heading font-extrabold text-amber-900 text-lg" data-testid="extended-repair-preview-date">
              {previewDeadline.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </div>
            <div className="text-[11px] text-slate-600 font-mono mt-1">
              {businessDays} día{businessDays === 1 ? "" : "s"} hábil{businessDays === 1 ? "" : "es"} desde hoy
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy} data-testid="extended-repair-cancel">
            Cancelar
          </button>
          <button
            className="btn-primary bg-amber-500 hover:bg-amber-400"
            onClick={submit}
            disabled={busy || businessDays < 1 || businessDays > 15}
            data-testid="extended-repair-save"
          >
            {busy ? "Guardando…" : (dev.extended_repair_active ? "Actualizar fecha" : "Marcar como extendida")}
          </button>
        </div>
      </div>
    </div>
  );
}

function PickupBlock({ order, onDone }) {
  const [open, setOpen] = useState(false);
  const [paidAmount, setPaidAmount] = useState("");
  const [method, setMethod] = useState("efectivo");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => {
    const devs = order.devices_all || [];
    const ready = devs.filter((d) => ["REPAIRED", "READY"].includes(d.status));
    return ready.map((d) => d.id);
  });
  const devs = order.devices_all || [];
  const isMultiDevice = devs.length > 1;
  const readyDevices = devs.filter((d) => ["REPAIRED", "READY"].includes(d.status));

  function toggleDevice(did) {
    setSelectedIds((prev) => (prev.includes(did) ? prev.filter((x) => x !== did) : [...prev, did]));
  }

  async function submit() {
    if (isMultiDevice && selectedIds.length === 0) {
      toast.error("Selecciona al menos un equipo a entregar.");
      return;
    }
    setBusy(true);
    try {
      const payload = {};
      if (paidAmount !== "") payload.paid_amount = Number(paidAmount);
      if (method) payload.payment_method = method;
      if (note.trim()) payload.note = note.trim();
      if (isMultiDevice) payload.device_ids = selectedIds;
      await api.post(`/orders/${order.id}/pickup`, payload);
      toast.success(isMultiDevice ? "Retiro parcial registrado" : "Retiro y cobro registrados");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-primary w-full py-3 bg-blue-600 hover:bg-blue-500"
        data-testid="open-pickup-btn"
      >
        {isMultiDevice ? "Registrar retiro / pago (seleccionar equipos)" : "Registrar retiro / pago del cliente"}
      </button>
    );
  }

  return (
    <div className="space-y-3 rounded-xl bg-white border border-slate-200 p-3" data-testid="pickup-form">
      {isMultiDevice && (
        <div>
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-2">
            Equipos a entregar en este retiro
          </label>
          <div className="space-y-1.5" data-testid="pickup-device-list">
            {devs.map((d, i) => {
              const isReady = ["REPAIRED", "READY"].includes(d.status);
              const isDelivered = d.status === "DELIVERED";
              const label = `${d.brand || ""} ${d.model || d.subcategory?.name || `Equipo #${i + 1}`}`.trim();
              const stLabel = STATUS_LABEL[d.status] || d.status;
              return (
                <label
                  key={d.id}
                  className={`flex items-start gap-2 rounded-lg border p-2 text-sm ${
                    isReady ? "border-emerald-300 bg-emerald-50 cursor-pointer" :
                    isDelivered ? "border-slate-200 bg-slate-50 opacity-60" :
                    "border-amber-200 bg-amber-50 opacity-70"
                  }`}
                  data-testid={`pickup-device-item-${i}`}
                >
                  <input
                    type="checkbox"
                    disabled={!isReady}
                    checked={isReady && selectedIds.includes(d.id)}
                    onChange={() => toggleDevice(d.id)}
                    className="mt-0.5"
                    data-testid={`pickup-device-check-${i}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-800 truncate">
                      #{i + 1} · {label}
                    </div>
                    <div className="text-[11px] font-mono text-slate-600">
                      {isReady ? "Listo para retiro" : isDelivered ? "Ya entregado" : `No disponible (${stLabel})`}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
          {readyDevices.length === 0 && (
            <div className="mt-2 text-xs text-amber-800 font-semibold" data-testid="pickup-no-ready">
              Ningún equipo está listo para retiro.
            </div>
          )}
        </div>
      )}
      <div>
        <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Monto cobrado</label>
        <input
          type="number"
          step="0.01"
          min="0"
          className="input-base font-mono"
          placeholder="0.00"
          value={paidAmount}
          onChange={(e) => setPaidAmount(e.target.value)}
          data-testid="pickup-amount"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Método</label>
        <select
          className="input-base"
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          data-testid="pickup-method"
        >
          <option value="efectivo">Efectivo</option>
          <option value="transferencia">Transferencia</option>
          <option value="pos">POS / Débito o Crédito</option>
          <option value="pago_movil">Pago móvil</option>
          <option value="otro">Otro</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Observación (opcional)</label>
        <input
          className="input-base"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Ej: cliente pagó en 2 partes"
          data-testid="pickup-note"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary flex-1 py-2.5"
          onClick={submit}
          disabled={busy || (isMultiDevice && selectedIds.length === 0)}
          data-testid="pickup-submit"
        >
          Confirmar retiro
        </button>
        <button
          type="button"
          className="btn-secondary py-2.5"
          onClick={() => setOpen(false)}
          disabled={busy}
          data-testid="pickup-cancel"
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}



// ===== AddDeviceInline: bloque compacto para agregar equipos adicionales =====
function AddDeviceInline({ orderId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    category_id: "",
    subcategory_id: "",
    brand: "",
    model: "",
    serial: "",
    accessories: "",
    problem_reported: "",
    technician_id: "",
  });

  useEffect(() => {
    if (!open) return;
    api.get("/categories").then((r) => setCategories((r.data || []).filter((x) => x.active)));
    api.get("/users").then((r) => setTechnicians((r.data || []).filter((x) => x.role === "technician" && x.active))).catch(() => {});
    api.get("/brands").then((r) => setBrands((r.data || []).filter((x) => x.active))).catch(() => setBrands([]));
  }, [open]);

  useEffect(() => {
    if (!form.category_id) { setSubcategories([]); return; }
    api.get(`/subcategories?category_id=${form.category_id}`)
      .then((r) => setSubcategories((r.data || []).filter((s) => s.active)))
      .catch(() => setSubcategories([]));
  }, [form.category_id]);

  // Auto-select default brand when the subcategory changes (mismo comportamiento del equipo primario)
  const selectedSub = useMemo(
    () => subcategories.find((s) => s.id === form.subcategory_id) || null,
    [subcategories, form.subcategory_id],
  );
  useEffect(() => {
    if (!selectedSub || !selectedSub.default_brand_id) return;
    const b = brands.find((x) => x.id === selectedSub.default_brand_id);
    if (b && form.brand !== b.name) {
      setForm((f) => ({ ...f, brand: b.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.subcategory_id, brands]);

  // Load models filtered por subcategoría + marca — mismo comportamiento del equipo primario
  useEffect(() => {
    if (!form.subcategory_id) { setModels([]); return; }
    const brandObj = brands.find((b) => b.name.toLowerCase() === (form.brand || "").toLowerCase());
    const brand_id = brandObj?.id;
    const params = new URLSearchParams({ subcategory_id: form.subcategory_id });
    if (brand_id) params.append("brand_id", brand_id);
    api.get(`/models?${params.toString()}`)
      .then((r) => setModels((r.data || []).filter((m) => m.active !== false)))
      .catch(() => setModels([]));
  }, [form.subcategory_id, form.brand, brands]);

  const brandOptions = useMemo(() => brands.map((b) => b.name), [brands]);
  const modelOptions = useMemo(() => models.map((m) => m.name), [models]);

  const catName = categories.find((c) => c.id === form.category_id)?.name || "";
  const needsSerial = ["Consolas", "Teléfonos"].includes(catName);

  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  function handleCategoryChange(newCatId) {
    setForm((f) => ({ ...f, category_id: newCatId, subcategory_id: "", brand: "", model: "" }));
  }
  function handleSubcategoryChange(newSubId) {
    setForm((f) => ({ ...f, subcategory_id: newSubId, brand: "", model: "" }));
  }
  function handleBrandChange(newBrand) {
    setForm((f) => {
      if (!newBrand || newBrand !== f.brand) return { ...f, brand: newBrand, model: "" };
      return { ...f, brand: newBrand };
    });
  }

  async function save() {
    if (!form.category_id || !form.subcategory_id || !form.problem_reported.trim()) {
      toast.error("Categoría, subcategoría y problema son obligatorios");
      return;
    }
    if (needsSerial && !form.serial.trim()) {
      toast.error(`Serial obligatorio para ${catName.toLowerCase()}`);
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.technician_id) delete payload.technician_id;
      await api.post(`/orders/${orderId}/devices`, payload);
      toast.success("Equipo agregado a la orden");
      setOpen(false);
      setForm({ category_id: "", subcategory_id: "", brand: "", model: "", serial: "", accessories: "", problem_reported: "", technician_id: "" });
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50/40 py-3 text-emerald-800 font-heading font-bold hover:bg-emerald-50 hover:border-emerald-400 transition-colors inline-flex items-center justify-center gap-2"
        data-testid="add-device-inline-open"
      >
        <Wrench size={16} /> + Agregar otro equipo a esta orden
      </button>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50/30 p-4 space-y-3" data-testid="add-device-inline">
      <div className="flex items-center justify-between">
        <div className="font-heading font-bold text-emerald-900">Agregar equipo a esta orden</div>
        <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-slate-800 text-sm" data-testid="add-device-inline-close">
          <X size={14} /> Cerrar
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-mono uppercase text-slate-500 mb-1">Categoría *</div>
          <select value={form.category_id} onChange={(e) => handleCategoryChange(e.target.value)} className="input-base py-2" data-testid="add-device-category">
            <option value="">—</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-slate-500 mb-1">Subcategoría *</div>
          <select value={form.subcategory_id} onChange={(e) => handleSubcategoryChange(e.target.value)} className="input-base py-2" disabled={!form.category_id} data-testid="add-device-subcategory">
            <option value="">—</option>
            {subcategories.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-slate-500 mb-1">Marca</div>
          <SearchableSelect
            options={brandOptions}
            value={form.brand}
            onChange={handleBrandChange}
            placeholder="Seleccionar marca…"
            testid="add-device-brand"
          />
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-slate-500 mb-1">Modelo</div>
          <SearchableSelect
            options={modelOptions}
            value={form.model}
            onChange={(v) => upd("model", v)}
            placeholder={form.subcategory_id ? "Seleccionar modelo…" : "Elegí subcategoría primero"}
            disabled={!form.subcategory_id}
            testid="add-device-model"
            emptyLabel="Sin modelos catalogados"
          />
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-slate-500 mb-1">{needsSerial ? "Serial / IMEI *" : "Serial / IMEI"}</div>
          <input value={form.serial} onChange={(e) => upd("serial", e.target.value)} className={`input-base py-2 font-mono ${needsSerial && !form.serial ? "border-amber-400" : ""}`} data-testid="add-device-serial" />
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-slate-500 mb-1">Accesorios</div>
          <input value={form.accessories} onChange={(e) => upd("accessories", e.target.value)} className="input-base py-2" data-testid="add-device-accessories" />
        </div>
        {technicians.length > 0 && (
          <div className="sm:col-span-2">
            <div className="text-xs font-mono uppercase text-slate-500 mb-1">Técnico</div>
            <select value={form.technician_id} onChange={(e) => upd("technician_id", e.target.value)} className="input-base py-2" data-testid="add-device-technician">
              <option value="">Sin asignar</option>
              {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div>
        <div className="text-xs font-mono uppercase text-slate-500 mb-1">Problema reportado *</div>
        <textarea value={form.problem_reported} onChange={(e) => upd("problem_reported", e.target.value)} className="input-base min-h-[70px]" placeholder="Describe el problema…" data-testid="add-device-problem" />
      </div>
      <button
        type="button"
        disabled={saving}
        onClick={save}
        className="btn-primary w-full"
        data-testid="add-device-inline-save"
      >
        <Save size={14} /> {saving ? "Guardando…" : "Agregar equipo a la orden"}
      </button>
    </div>
  );
}


// =====================================================================
// Botón "Tomar reparación" (self-assign) — visible solo al Técnico cuando
// el scope (orden primaria o device secundario) no tiene técnico asignado.
// =====================================================================
function ClaimRepairButton({ orderId, deviceId, onClaimed }) {
  const [saving, setSaving] = useState(false);
  async function claim() {
    if (!window.confirm("¿Tomar esta reparación? Quedarás registrado como técnico responsable.")) return;
    setSaving(true);
    try {
      const body = {};
      if (deviceId) body.device_id = deviceId;
      await api.post(`/orders/${orderId}/claim`, body);
      toast.success("Te asignaste esta reparación");
      onClaimed?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="mt-3" data-testid="claim-repair-wrapper">
      <div className="text-xs text-amber-800 mb-2">Esta reparación aún no tiene técnico responsable.</div>
      <button
        type="button"
        onClick={claim}
        disabled={saving}
        className="w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-heading font-bold bg-amber-600 text-white hover:bg-amber-500 transition-colors disabled:opacity-60"
        data-testid="claim-repair-button"
      >
        🙋 {saving ? "Tomando…" : "Tomar esta reparación"}
      </button>
    </div>
  );
}

// =====================================================================
// Reparto financiero por orden/device — visible solo si el usuario tiene
// la capability `finance.view_costs`. Reusa el endpoint
// GET /orders/{id}/financial-breakdown que, para órdenes finalizadas, devuelve
// los valores congelados; para órdenes abiertas, calcula al vuelo.
// =====================================================================
function FinancialBreakdownCard({ orderId, deviceId, multi = false }) {
  const canView = useCap("finance.view_costs");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [items, setItems] = useState([]);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const bump = () => setReload((x) => x + 1);
    window.addEventListener(COSTS_UPDATED_EVENT, bump);
    return () => window.removeEventListener(COSTS_UPDATED_EVENT, bump);
  }, []);

  useEffect(() => {
    if (!canView || !isAdmin) return;
    api.get(`/orders/${orderId}/budget`, { params: { device_id: deviceId || orderId } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => {});
  }, [orderId, deviceId, canView, isAdmin, reload]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    const params = {};
    if (deviceId) params.device_id = deviceId;
    api.get(`/orders/${orderId}/financial-breakdown`, { params })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [orderId, deviceId, canView, reload]);

  if (!canView) return null;
  if (loading && !data) return (
    <div className="mt-4 card-base p-4 text-xs text-slate-500" data-testid="financial-breakdown-loading">
      Calculando reparto financiero…
    </div>
  );
  if (err) return (
    <div className="mt-4 card-base p-4 text-xs text-red-700" data-testid="financial-breakdown-error">
      {err}
    </div>
  );
  if (!data) return null;

  const b = data.breakdown || {};
  const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);
  const pct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);
  const frozen = !!data.frozen;
  const isPending = b.status === "PENDING" || b.parts_policy === "PENDING";
  const hasOp = (b.operational_cost || 0) > 0;

  return (
    <div className="mt-4 card-base p-5" data-testid="financial-breakdown-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-heading font-bold text-slate-900 text-base">Reparto financiero</h3>
        <span
          className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full ${
            isPending ? "bg-amber-200 text-amber-900"
            : frozen ? "bg-emerald-100 text-emerald-800"
            : "bg-amber-100 text-amber-800"
          }`}
          data-testid="financial-breakdown-status"
        >
          {isPending ? "Pendiente" : data.regularized ? "Regularizado" : (frozen ? "Congelado al finalizar" : "Vista previa")}
        </span>
      </div>
      {isPending ? (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-1" data-testid="financial-breakdown-pending">
          <div className="font-semibold">⚠️ Cálculo financiero pendiente por costo de repuesto</div>
          {b.pending_reason && <div className="text-xs">{b.pending_reason}</div>}
          <div className="text-xs">{isAdmin ? "Completá la clasificación y el costo real en «Repuestos por servicio» (abajo)." : "Administración debe confirmar el costo de repuestos para completar el reparto."}</div>
          <div className="pt-2 text-xs font-mono text-amber-900">{multi ? "Precio del equipo" : "Precio final"}: <b>{money(b.final_price)}</b></div>
        </div>
      ) : (
        <>
          <dl className="divide-y divide-slate-100 text-sm">
            <Row label={multi ? "Precio del equipo" : "Precio final"} value={money(b.final_price)} testid="fb-final-price" bold />
            <Row label="Costo de repuestos" value={money(b.parts_cost)} testid="fb-parts-cost" />
            {hasOp && (
              <Row
                label={`Costo operativo del taller (${pct(b.operational_pct_applied)})`}
                value={money(b.operational_cost)}
                testid="fb-operational-cost"
              />
            )}
            <Row label="Base repartible" value={money(b.distributable_base)} testid="fb-distributable-base" bold />
            <Row
              label={`% técnico aplicado${b.technician_pct_applied == null ? " (sin configurar)" : ""}`}
              value={pct(b.technician_pct_applied)}
              testid="fb-tech-pct"
            />
            <Row label="Monto técnico" value={money(b.technician_payout)} testid="fb-tech-payout" />
            <Row label="Monto empresa" value={money(b.company_share)} testid="fb-company-share" bold />
          </dl>
          {b.rounding_rule && (
            <div className="mt-2 text-[11px] text-slate-500">
              Redondeo: dólares enteros con <b>ceil</b> hacia arriba · residuo asignado a la empresa.
            </div>
          )}
        </>
      )}
      {data.regularized && data.regularization && (
        <div className="mt-3 text-[11px] text-indigo-700" data-testid="financial-breakdown-regularized">
          Reparto completado por regularización · {data.regularization.applied_by_name} · {data.regularization.reason}
        </div>
      )}
      {isAdmin && items.length > 0 && (
        <div className="mt-4 pt-3 border-t border-slate-200" data-testid="financial-parts-editor">
          <div className="text-xs font-semibold text-slate-600 mb-1">Repuestos por servicio</div>
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.id} className="text-sm" data-testid={`financial-parts-item-${it.id}`}>
                <div className="flex justify-between gap-2">
                  <span className="truncate text-slate-800">{it.name}{!it.service_id && <span className="ml-1 text-[10px] font-mono text-amber-700">CUSTOM</span>}</span>
                  <span className="font-mono text-slate-700 shrink-0">{money(it.subtotal)}</span>
                </div>
                <PartsCostEditor orderId={orderId} item={it} />
              </li>
            ))}
          </ul>
          {frozen && <div className="mt-2 text-[11px] text-slate-500">Equipo finalizado: los cambios se aplican como regularización financiera, sin alterar la producción histórica.</div>}
        </div>
      )}
      {frozen && data.finalized_at && (
        <div className="mt-3 text-[11px] text-slate-500">
          Valores congelados al finalizar la reparación · {new Date(data.finalized_at).toLocaleString("es-AR")}
        </div>
      )}
      {!frozen && !isPending && (
        <div className="mt-3 text-[11px] text-slate-500">
          Vista previa — estos valores se congelarán cuando la reparación se finalice.
        </div>
      )}
    </div>
  );
}

function Row({ label, value, testid, bold }) {
  return (
    <div className="flex items-center justify-between py-1.5" data-testid={testid}>
      <dt className={`text-slate-600 ${bold ? "font-semibold" : ""}`}>{label}</dt>
      <dd className={`font-mono ${bold ? "font-bold text-slate-900" : "text-slate-800"}`}>{value}</dd>
    </div>
  );
}

/**
 * FinishedDeviceModal — informa al usuario que un equipo quedó finalizado y
 * lista los equipos restantes pendientes de la orden multi-equipo, con un
 * botón para saltar al próximo equipo pendiente.
 */
function FinishedDeviceModal({ order, finishedDevice, pending, onGoToNext, onClose }) {
  const label = finishedDevice
    ? `${finishedDevice.brand || ""} ${finishedDevice.model || finishedDevice.subcategory?.name || "Equipo"}`.trim()
    : "equipo";
  const total = (order.devices_all || []).length;
  const activeIdx = (order.devices_all || []).findIndex((d) =>
    finishedDevice && d.id === finishedDevice.id);
  const activeNum = activeIdx >= 0 ? activeIdx + 1 : 1;
  const pendingCount = pending.length;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl p-5 max-w-md w-full space-y-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="finished-device-modal"
      >
        <div className="font-heading font-extrabold text-lg text-emerald-900">
          ✅ Equipo #{activeNum} finalizado correctamente
        </div>
        <div className="text-sm text-slate-700">
          <b>{label}</b> quedó reparado/listo para retirar.
        </div>
        {pendingCount > 0 ? (
          <>
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="finished-device-pending-block">
              <div className="font-bold mb-1">
                ⚠️ Esta orden contiene {total} equipos. {pendingCount === 1 ? "Queda 1 equipo pendiente" : `Quedan ${pendingCount} equipos pendientes`}:
              </div>
              <ul className="list-disc pl-5 text-xs space-y-0.5">
                {pending.map((p) => {
                  const l = `${p.brand || ""} ${p.model || p.subcategory?.name || "Equipo"}`.trim();
                  const st = STATUS_LABEL[p.status] || p.status;
                  return <li key={p.id}><b>{l}</b> — {st}</li>;
                })}
              </ul>
              <div className="text-xs text-amber-800 mt-2">
                La orden completa permanecerá abierta hasta resolver los demás equipos.
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-primary flex-1 bg-emerald-600 hover:bg-emerald-500"
                onClick={onGoToNext}
                data-testid="finished-device-goto-next"
              >
                Ir al siguiente equipo pendiente
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                data-testid="finished-device-stay"
              >
                Quedarme aquí
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
              ✅ Todos los equipos de esta orden han sido finalizados ({total}). Los equipos están listos para continuar con el proceso de entrega/retiro correspondiente.
            </div>
            <button
              type="button"
              className="btn-primary w-full"
              onClick={onClose}
              data-testid="finished-device-close-all-done"
            >
              Entendido
            </button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * ReconcileTriggerButton — se muestra sólo si el usuario tiene la capability
 * `orders.correct_pickup`. Útil para corregir órdenes multi-equipo que quedaron
 * en estados estructuralmente incoherentes por el flujo anterior.
 */
function ReconcileTriggerButton({ order, onClick }) {
  const canCorrect = useCap("orders.correct_pickup");
  if (!canCorrect) return null;
  // Detectar inconsistencia típica: orden madre DELIVERED pero algún device no DELIVERED,
  // o devices con estados mezclados que impiden continuar el flujo.
  const devs = order.devices_all || [];
  const isDelivered = order.status === "DELIVERED";
  const hasActiveDevice = devs.some((d) => d.status !== "DELIVERED");
  const inconsistent = isDelivered && hasActiveDevice;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-heading font-bold transition-colors ${
        inconsistent
          ? "bg-amber-600 text-white hover:bg-amber-500"
          : "bg-white text-amber-700 border border-amber-300 hover:bg-amber-50"
      }`}
      data-testid="reconcile-device-trigger"
      title={inconsistent
        ? "La orden madre figura entregada pero hay equipos activos. Reconciliar."
        : "Corregir manualmente el estado de un equipo (auditado)."}
    >
      ⚠️ Corregir estado multi-equipo{inconsistent ? " (incoherencia detectada)" : ""}
    </button>
  );
}

/**
 * ReconcileDeviceModal — permite al Admin reconciliar el estado de un device
 * sin alterar producción. Toda acción queda registrada en el historial.
 */
function ReconcileDeviceModal({ order, onClose, onDone }) {
  const devs = order.devices_all || [];
  const [deviceId, setDeviceId] = useState(devs[0]?.id || "");
  const [newStatus, setNewStatus] = useState("REPAIRED");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const selected = devs.find((d) => d.id === deviceId);

  async function submit() {
    const r = reason.trim();
    if (r.length < 3) {
      toast.error("El motivo es obligatorio (mínimo 3 caracteres).");
      return;
    }
    if (!deviceId) {
      toast.error("Selecciona un equipo.");
      return;
    }
    if (!window.confirm(`¿Reconciliar el equipo seleccionado a estado "${STATUS_LABEL[newStatus] || newStatus}"? No se altera production_snapshot ni finalized_at.`)) return;
    setSaving(true);
    try {
      await api.post(`/orders/${order.id}/admin/reconcile-device`, {
        device_id: deviceId,
        new_status: newStatus,
        reason: r,
      });
      toast.success("Estado del equipo reconciliado");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  // Estados elegibles — igual a ORDER_STATUSES visibles al usuario
  const STATUS_OPTIONS = [
    "RECEIVED", "IN_DIAGNOSIS", "WAITING_APPROVAL", "WAITING_PARTS",
    "IN_REPAIR", "TESTING", "REPAIRED", "READY", "DELIVERED",
    "IN_WARRANTY", "NOT_REPAIRABLE", "CLIENT_REJECTED",
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !saving && onClose()}>
      <div
        className="bg-white rounded-2xl p-5 max-w-md w-full space-y-3"
        onClick={(e) => e.stopPropagation()}
        data-testid="reconcile-device-modal"
      >
        <div className="font-heading font-extrabold text-lg text-slate-900">
          ⚠️ Corregir estado multi-equipo
        </div>
        <div className="text-xs text-slate-600">
          Acción administrativa auditada. <b>No altera</b> <code>production_snapshot</code> ni <code>finalized_at</code>.
          Si tras la corrección la orden madre deja de ser consistente con sus equipos, se degradará automáticamente.
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Equipo</label>
          <select
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            className="input-base"
            data-testid="reconcile-device-select"
          >
            {devs.map((d, i) => {
              const label = `${d.brand || ""} ${d.model || d.subcategory?.name || `Equipo #${i + 1}`}`.trim();
              return (
                <option key={d.id} value={d.id}>
                  #{i + 1} · {label} · {STATUS_LABEL[d.status] || d.status}
                </option>
              );
            })}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">
            Nuevo estado{selected ? ` (actual: ${STATUS_LABEL[selected.status] || selected.status})` : ""}
          </label>
          <select
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            className="input-base"
            data-testid="reconcile-new-status"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-mono text-slate-500 block mb-1">Motivo *</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej.: El retiro del Equipo #1 cerró incorrectamente la orden. El Equipo #2 continúa en reparación."
            className="input-base min-h-[80px] text-sm"
            data-testid="reconcile-reason"
          />
        </div>
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn-secondary flex-1"
            data-testid="reconcile-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving || !reason.trim()}
            className="btn-primary flex-1 bg-amber-600 hover:bg-amber-500 disabled:opacity-50"
            data-testid="reconcile-submit"
          >
            {saving ? "Guardando…" : "Aplicar corrección"}
          </button>
        </div>
      </div>
    </div>
  );
}

